/**
 * Pass-Through Diagram Library (SHARED)
 *
 * Single source of truth for the four isometric door diagrams and the
 * single-door pass-through math. Consumed by BOTH:
 *   - assets/pass-through-widget.js  (the /pages/measurement-charts + PDP widget)
 *   - assets/md-config-ui-pass-through-diagram.js (the configurator PTS tooltip)
 *
 * WHY THIS FILE EXISTS: these SVGs and this math were previously inlined in
 * pass-through-widget.js only. The configurator needed the same diagrams, and
 * per CLAUDE.md the pass-through numbers already live in too many places — so
 * the geometry and the math are centralised here rather than copied a fifth time.
 *
 * 🚨 SINGLE DOORS ONLY. The math here follows engineering's single-door sheet
 * (R. Cooley, rev. 2026-09-25): jamb-aware wall clearance (½ / 2¼ / 4 / 6″ at
 * the standard 4-9/16″ jamb) and jamb/casing-aware in-swing pass-through
 * (W − 7-5/16″ at the standard jamb and casing). French doors keep their own ½″-reveal formula in
 * assets/french-door-widget.js — the sheet does not cover French. Do NOT apply
 * these values to French doors — see the "Pass-Through Math" footgun in
 * .claude/docs/known-footguns.md.
 *
 * DIVISION OF LABOUR: this library owns geometry, the label slots, and the raw
 * math. It does NOT own formatting — callers pass display strings into
 * applyMeasurements() so each surface keeps its own established number style.
 *
 * SVG source: edit the DXF files and re-run scripts/build_ptw_widget.py.
 */
(function (window) {
  'use strict';

  // ── PASS-THROUGH MATH ─────────────────────────────────────────────────
  // SOURCE OF TRUTH: engineering's "Door Measurement Calculator" sheet
  // (R. Cooley, rev. 2026-09-25), scope FMD2 SINGLE door. The configurator's
  // copy lives in md-config-bl-space-calculator.js; both are pinned to the
  // sheet by tests/assets/cooley-sheet-oracle.test.js.
  //
  // Out-swing pass-through = W − (depth + 3.5)  (sheet B21). Was depth + 3
  // until 2026-09-25, which over-reported by ½″ at every depth.
  var OS_SPECS = {
    5.25: { reduction: 8.75 },
    6.5: { reduction: 10 },
    8.25: { reduction: 11.75 },
    10: { reduction: 13.5 },
    12: { reduction: 15.5 },
  };

  // Standard jamb (2x4 wall, ½″ drywall). These surfaces have no jamb input,
  // so wall clearance is stated for the standard jamb.
  var STANDARD_JAMB_DEPTH = 4.5625;

  // In-swing back-side wall clearance, sheet B23 (rev. 2026-09-25), measured
  // from the outside edge of the jamb to the back corner of the open door:
  //   depth == 8¼ AND jamb > 4-9/16 : depth + 0.75 − jamb − 1.4375
  //   otherwise                     : depth − jamb − 1.4375
  // At the standard jamb that is ½ / 2¼ / 4 / 6″ for 6½ / 8¼ / 10 / 12″.
  // Replaces max(0, floor(depth − 6.5) + 0.75), which assumed a 6.5″ jamb and
  // under-reported by up to 1-5/16″. Clamped at 0 (door clears inside the jamb).
  // Out-swing doors swing away from the wall — no clearance required on the wall side.
  function calcWallClearance(swing, depth, jamb) {
    if (swing === 'out') return 0;
    depth = Number(depth);
    var j = isFinite(jamb) && jamb > 0 ? jamb : STANDARD_JAMB_DEPTH;
    var raw =
      depth === 8.25 && j > STANDARD_JAMB_DEPTH
        ? depth + 0.75 - j - 1.4375
        : depth - j - 1.4375;
    return Math.max(0, Math.round(raw * 16) / 16);
  }

  function fmtInches(v) {
    if (v === 0) return '0″';
    if (!v || !isFinite(v)) return '—';
    var whole = Math.floor(v);
    var frac = Math.round((v - whole) * 4) / 4;
    var fracStr = frac === 0 ? '' : frac === 0.25 ? '¼' : frac === 0.5 ? '½' : '¾';
    return (whole > 0 ? String(whole) : '') + fracStr + '″';
  }

  // Fraction formatter for pass-through figures, which land on sixteenths now
  // that in-swing depends on the jamb (28.6875 -> '28-11/16″'). Hyphenated like
  // the jamb caption ('4-9/16"'); fmtInches above stays quarter-glyph for the
  // wall-clearance readout, whose standard-jamb values are all quarters.
  function fmtInches16(v) {
    if (!isFinite(v)) return '—';
    var units = Math.round(Math.abs(v) * 16);
    var whole = Math.floor(units / 16);
    var num = units - whole * 16;
    var den = 16;
    while (num > 0 && num % 2 === 0) {
      num /= 2;
      den /= 2;
    }
    var sign = v < 0 ? '-' : '';
    if (num === 0) return sign + whole + '″';
    return sign + (whole > 0 ? whole + '-' : '') + num + '/' + den + '″';
  }

  // Casing widths for the "-1 RULE" (sheet B10 as deployed: wide when the
  // rule is met, EXCEPT Fluted, which is vendor-milled and always 3¾″).
  var CASING_WIDTH_STANDARD = 3.75;
  var CASING_WIDTH_WIDE = 4.75;

  // In-swing pass-through, sheet B24 (R. Cooley, FINAL 2026-09-25):
  //   W − (jamb + 0.5) − (casingWidth − 1.5)
  // casingWidth = 4¾″ when jamb > 4-9/16 (the in-swing arm of the "-1 RULE")
  // and the casing is not Fluted; else 3¾″. These surfaces have no jamb or
  // casing input, so callers without one get the standard 4-9/16″ jamb and
  // Standard casing: W − 7-5/16″. Replaces the flat W − 6. The configurator
  // copy lives in md-config-bl-space-calculator.js (computeInSwingPassThrough);
  // both are pinned to the sheet by tests/assets/cooley-sheet-oracle.test.js.
  function inSwingReduction(jamb, fluted) {
    var j = isFinite(jamb) && jamb > 0 ? jamb : STANDARD_JAMB_DEPTH;
    var casing =
      j > STANDARD_JAMB_DEPTH && fluted !== true
        ? CASING_WIDTH_WIDE
        : CASING_WIDTH_STANDARD;
    return j + 0.5 + (casing - 1.5);
  }

  function calcPassThrough(swing, depth, width, jamb, fluted) {
    if (swing === 'in') {
      return Math.round((width - inSwingReduction(jamb, fluted)) * 16) / 16;
    } else {
      var spec = OS_SPECS[String(depth)];
      return spec ? width - spec.reduction : width - OS_SPECS[8.25].reduction;
    }
  }
  // Swing clearance, sheet B24 (R. Cooley, rev. 2026-09-25 evening): "the
  // distance from the wall to the furthest point on the door when open".
  //   In-Swing:  casingWidth == 3-3/4 ? W - 1.25 : W - 2.25
  //   Out-Swing: W - 4
  // casingWidth is the same "-1 RULE" casing inSwingReduction uses. These
  // surfaces have no jamb or casing input, so callers without one get the
  // standard 4-9/16" jamb and Standard casing: in-swing W - 1.25. NOT the wall
  // clearance above (sheet B25) -- a different quantity. The configurator copy
  // lives in md-config-bl-space-calculator.js (computeSwingClearance); both are
  // pinned to the sheet by tests/assets/cooley-sheet-oracle.test.js.
  function calcSwingClearance(swing, width, jamb, fluted) {
    width = Number(width);
    if (swing !== 'in') return Math.round((width - 4) * 16) / 16;
    var j = isFinite(jamb) && jamb > 0 ? jamb : STANDARD_JAMB_DEPTH;
    var casing =
      j > STANDARD_JAMB_DEPTH && fluted !== true
        ? CASING_WIDTH_WIDE
        : CASING_WIDTH_STANDARD;
    return Math.round((width - (casing === CASING_WIDTH_WIDE ? 2.25 : 1.25)) * 16) / 16;
  }

  // ── SVG LIBRARY ───────────────────────────────────────────────────────
  // SVGs are inlined here for self-containment — no async fetch, no external deps.
  // Trade-off: 113KB snippet vs. independent deployability anywhere (product descriptions,
  // sections, snippets, metafields). If future SVG updates are needed, edit the source
  // DXF files and re-run the build script at scripts/build_ptw_widget.py.
  var SVGS = {
    'lh-in-iso': `<svg xmlns="http://www.w3.org/2000/svg" width="248" height="500" viewBox="0 0 215 500" overflow="visible">
<rect width="215" height="500" fill="white"/>
<g fill="none" stroke="#1b1d1f" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M 71.70,383.00 A 0.52,0.30 -180.00 1,1 72.58,383.00"/>
<path d="M 71.39,382.97 A 0.75,0.43 -0.00 1,1 71.39,382.97"/>
<path d="M 68.99,381.60 A 0.75,0.43 -0.00 1,1 68.99,381.60"/>
<path d="M 73.54,380.03 A 0.75,0.43 -180.00 0,1 72.47,379.41"/>
<path d="M 71.73,379.84 A 0.75,0.43 -0.00 0,1 72.80,380.45"/>
<path d="M 68.32,381.24 A 1.57,0.90 -0.00 0,1 68.32,382.51"/>
<path d="M 70.55,383.79 A 1.57,0.90 -0.00 0,1 72.77,383.79"/>
<path d="M 82.40,378.25 A 1.57,0.90 -0.00 0,1 82.67,377.18"/>
<path d="M 81.13,377.80 A 2.64,1.52 -0.00 0,1 78.57,376.41"/>
<path d="M 81.38,377.92 A 0.75,0.43 -180.00 0,1 82.45,377.31"/>
<path d="M 74.93,379.55 A 0.75,0.43 -0.00 1,1 74.93,379.55"/>
<path d="M 74.13,381.22 A 0.75,0.43 -180.00 0,1 75.19,381.83"/>
<path d="M 75.94,381.40 A 0.75,0.43 -180.00 0,1 74.87,380.79"/>
<path d="M 69.31,381.62 A 0.52,0.30 -180.00 1,1 70.19,381.62"/>
<path d="M 74.22,381.55 A 0.52,0.30 -180.00 0,1 74.29,381.93"/>
<path d="M 75.03,381.50 A 0.52,0.30 -0.00 0,1 75.79,381.08"/>
<path d="M 72.64,380.12 A 0.52,0.30 -0.00 0,1 73.40,379.71"/>
<path d="M 71.83,380.17 A 0.52,0.30 -0.00 0,1 71.90,380.55"/>
<path d="M 73.10,380.28 A 0.52,0.30 -180.00 0,1 72.64,380.36"/>
<path d="M 75.25,379.58 A 0.52,0.30 -180.00 1,1 76.12,379.58"/>
<path d="M 81.44,377.62 A 0.52,0.30 -0.00 0,1 81.40,377.65"/>
<path d="M 82.10,378.05 A 0.52,0.30 -0.00 0,1 82.32,377.62"/>
<path d="M 75.50,381.66 A 0.52,0.30 -180.00 0,1 75.03,381.74"/>
<path d="M 68.32,382.09 A 1.57,0.90 -0.00 0,1 67.86,382.73"/>
<path d="M 72.77,383.37 A 1.57,0.90 -0.00 0,1 70.55,383.37"/>
<path d="M 82.86,378.47 A 1.57,0.90 -0.00 0,1 82.40,377.83"/>
<path d="M 78.50,376.69 A 0.94,0.54 -180.00 1,1 78.50,376.69"/>
<path d="M 88.44,386.85 A 0.26,0.15 -180.00 1,1 88.44,386.85"/>
<path d="M 76.31,390.86 A 0.26,0.15 -0.00 1,1 76.31,390.86"/>
<path d="M 85.55,388.52 A 0.26,0.15 -0.00 1,1 85.55,388.52"/>
<path d="M 86.04,385.48 A 0.26,0.15 -0.00 1,1 86.04,385.48"/>
<path d="M 79.09,392.46 A 0.26,0.15 -0.00 1,1 79.09,392.46"/>
<path d="M 83.15,387.14 A 0.26,0.15 -0.00 1,1 83.15,387.14"/>
<line x1="161.16" y1="419.77" x2="98.24" y2="383.53"/>
<line x1="174.50" y1="447.97" x2="182.86" y2="443.15"/>
<line x1="182.86" y1="166.08" x2="182.86" y2="443.15"/>
<line x1="182.86" y1="166.08" x2="180.64" y2="164.80"/>
<line x1="174.97" y1="170.63" x2="182.86" y2="166.08"/>
<line x1="172.75" y1="169.35" x2="180.64" y2="164.80"/>
<path d="M 83.06,116.36 A 0.26,0.15 -0.00 1,1 83.06,116.36"/>
<path d="M 69.75,108.09 A 0.25,0.14 -180.00 1,1 69.75,108.09"/>
<path d="M 75.03,107.81 A 0.25,0.14 -0.00 1,1 75.03,107.81"/>
<path d="M 73.33,110.97 A 0.26,0.15 -0.00 1,1 73.33,110.97"/>
<path d="M 72.14,109.47 A 0.25,0.14 -0.00 1,1 72.14,109.47"/>
<path d="M 72.64,106.43 A 0.25,0.14 -180.00 1,1 72.64,106.43"/>
<path d="M 76.22,112.64 A 0.26,0.15 -0.00 1,1 76.22,112.64"/>
<path d="M 78.67,116.56 A 1.57,0.90 -0.00 0,1 80.89,115.28"/>
<line x1="172.75" y1="169.35" x2="180.64" y2="164.80"/>
<line x1="166.02" y1="83.27" x2="166.02" y2="106.34"/>
<line x1="166.02" y1="133.33" x2="166.02" y2="145.70"/>
<line x1="167.23" y1="48.73" x2="166.02" y2="49.42"/>
<line x1="165.56" y1="47.77" x2="167.23" y2="48.73"/>
<line x1="164.35" y1="48.46" x2="165.56" y2="47.77"/>
<line x1="164.35" y1="48.46" x2="162.87" y2="47.61"/>
<line x1="162.87" y1="47.61" x2="162.87" y2="49.31"/>
<line x1="186.57" y1="61.25" x2="166.02" y2="49.42"/>
<line x1="165.09" y1="46.33" x2="162.87" y2="47.61"/>
<line x1="188.79" y1="59.98" x2="165.09" y2="46.33"/>
<path d="M 170.50,58.91 A 0.26,0.15 -0.00 1,1 170.50,58.91"/>
<path d="M 177.46,65.90 A 0.26,0.15 -0.00 1,1 177.46,65.90"/>
<path d="M 169.52,56.16 A 0.26,0.15 -0.00 1,1 169.52,56.16"/>
<path d="M 172.41,54.49 A 0.26,0.15 -0.00 1,1 172.41,54.49"/>
<path d="M 180.24,64.30 A 0.26,0.15 -0.00 1,1 180.24,64.30"/>
<path d="M 168.11,60.29 A 0.26,0.15 -0.00 1,1 168.11,60.29"/>
<path d="M 176.27,65.90 A 0.26,0.15 -0.00 1,1 176.27,65.90"/>
<path d="M 171.00,61.96 A 0.26,0.15 -0.00 1,1 171.00,61.96"/>
<path d="M 171.91,57.54 A 0.26,0.15 -0.00 1,1 171.91,57.54"/>
<path d="M 174.80,55.87 A 0.26,0.15 -180.00 1,1 174.80,55.87"/>
<path d="M 176.86,64.71 A 1.57,0.90 -0.00 1,1 176.86,64.71"/>
<path d="M 173.39,60.58 A 0.26,0.15 -0.00 1,1 173.39,60.58"/>
<path d="M 173.48,64.30 A 0.26,0.15 -0.00 1,1 173.48,64.30"/>
<path d="M 101.55,108.72 A 0.26,0.15 -0.00 1,1 101.91,108.51"/>
<path d="M 98.76,107.12 A 0.26,0.15 -0.00 1,1 99.12,106.91"/>
<line x1="186.57" y1="61.25" x2="165.93" y2="49.37"/>
<line x1="165.56" y1="47.77" x2="167.04" y2="48.62"/>
<line x1="167.04" y1="48.84" x2="167.04" y2="48.62"/>
<path d="M 76.83,379.26 A 1.57,0.90 -0.00 0,1 79.05,379.26"/>
<path d="M 74.14,377.34 A 1.57,0.90 -0.00 0,1 74.60,377.98"/>
<path d="M 78.50,376.75 A 1.01,0.58 -0.00 1,1 78.50,376.75"/>
<path d="M 75.12,376.81 A 0.52,0.30 -0.00 1,1 75.12,376.81"/>
<path d="M 77.91,378.41 A 0.52,0.30 -180.00 1,1 77.91,378.41"/>
<path d="M 74.60,375.85 A 1.57,0.90 -180.00 0,1 74.60,377.13"/>
<path d="M 76.83,378.41 A 1.57,0.90 -0.00 0,1 79.05,378.41"/>
<line x1="161.16" y1="313.33" x2="98.35" y2="349.51"/>
<line x1="161.16" y1="212.27" x2="98.35" y2="248.44"/>
<line x1="192.50" y1="57.84" x2="194.65" y2="59.08"/>
<line x1="192.50" y1="57.84" x2="185.83" y2="61.68"/>
<line x1="187.98" y1="62.92" x2="185.83" y2="61.68"/>
<line x1="194.65" y1="59.08" x2="187.98" y2="62.92"/>
<line x1="194.65" y1="328.01" x2="194.65" y2="175.96"/>
<line x1="194.65" y1="162.18" x2="194.65" y2="59.08"/>
<line x1="187.98" y1="158.34" x2="187.98" y2="62.92"/>
<line x1="194.65" y1="328.01" x2="193.97" y2="328.40"/>
<line x1="185.83" y1="71.86" x2="185.83" y2="157.11"/>
<line x1="98.35" y1="375.76" x2="98.35" y2="383.47"/>
<line x1="98.35" y1="375.76" x2="161.16" y2="339.59"/>
<line x1="161.16" y1="337.11" x2="98.35" y2="373.29"/>
<line x1="98.35" y1="383.47" x2="161.16" y2="347.30"/>
<line x1="98.35" y1="141.91" x2="98.35" y2="383.47"/>
<line x1="187.98" y1="70.63" x2="111.82" y2="114.48"/>
<line x1="187.98" y1="70.63" x2="187.98" y2="62.92"/>
<line x1="187.98" y1="62.92" x2="105.13" y2="110.63"/>
<line x1="187.98" y1="62.92" x2="185.83" y2="61.68"/>
<line x1="102.98" y1="109.39" x2="185.83" y2="61.68"/>
<line x1="172.28" y1="455.05" x2="174.50" y2="453.77"/>
<line x1="161.16" y1="448.65" x2="172.28" y2="455.05"/>
<line x1="172.28" y1="455.05" x2="172.28" y2="184.48"/>
<line x1="172.28" y1="184.48" x2="161.16" y2="178.08"/>
<line x1="161.16" y1="448.65" x2="161.16" y2="178.08"/>
<line x1="174.50" y1="184.91" x2="174.50" y2="453.77"/>
<line x1="172.28" y1="184.48" x2="172.28" y2="184.48"/>
<line x1="161.16" y1="178.08" x2="161.16" y2="178.08"/>
<line x1="173.76" y1="172.49" x2="173.76" y2="185.33"/>
<line x1="175.98" y1="171.21" x2="173.76" y2="172.49"/>
<line x1="175.98" y1="184.05" x2="173.76" y2="185.33"/>
<line x1="175.98" y1="171.21" x2="175.98" y2="184.05"/>
<line x1="98.35" y1="274.70" x2="98.35" y2="282.40"/>
<line x1="161.16" y1="246.23" x2="98.35" y2="282.40"/>
<line x1="161.16" y1="236.05" x2="98.35" y2="272.22"/>
<line x1="98.35" y1="274.70" x2="161.16" y2="238.52"/>
<line x1="195.46" y1="162.65" x2="193.23" y2="163.93"/>
<line x1="193.23" y1="163.93" x2="193.23" y2="176.77"/>
<line x1="193.23" y1="176.77" x2="195.46" y2="175.49"/>
<line x1="195.46" y1="175.49" x2="195.46" y2="162.65"/>
<line x1="182.86" y1="170.80" x2="193.23" y2="176.77"/>
<line x1="191.75" y1="175.92" x2="182.86" y2="170.80"/>
<line x1="193.97" y1="176.35" x2="193.97" y2="446.99"/>
<line x1="191.75" y1="448.27" x2="191.75" y2="175.92"/>
<line x1="193.97" y1="446.99" x2="191.75" y2="448.27"/>
<line x1="191.75" y1="448.27" x2="182.86" y2="443.15"/>
<line x1="145.98" y1="169.34" x2="98.35" y2="196.77"/>
<line x1="161.16" y1="187.80" x2="98.35" y2="223.97"/>
<line x1="98.35" y1="221.40" x2="161.16" y2="185.23"/>
<line x1="185.83" y1="117.75" x2="165.83" y2="106.23"/>
<line x1="165.83" y1="106.23" x2="131.66" y2="125.91"/>
<line x1="101.11" y1="143.50" x2="98.35" y2="145.09"/>
<line x1="185.83" y1="121.92" x2="155.28" y2="139.51"/>
<line x1="124.73" y1="157.10" x2="98.35" y2="172.30"/>
<line x1="98.35" y1="169.73" x2="122.50" y2="155.82"/>
<line x1="153.05" y1="138.23" x2="185.83" y2="119.35"/>
<line x1="161.16" y1="264.09" x2="98.35" y2="300.26"/>
<line x1="161.16" y1="291.29" x2="98.35" y2="327.46"/>
<line x1="98.35" y1="324.89" x2="161.16" y2="288.72"/>
</g>
<polygon points="-107.13,501.8 161.16,347.3 161.2,443.7 -107.13,598.3" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<text class="ptw-rug-label" x="84.0" y="440.0" fill="#842424" font-size="20" font-weight="700" font-family="'GT America Mono Light',monospace" text-anchor="middle" dominant-baseline="middle">-</text>
<polygon points="193.97,328.4 355.13,235.6 355.13,332.0 193.97,424.8" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<g fill="none" stroke="#1b1d1f" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<line x1="72.80" y1="380.45" x2="73.54" y2="380.03"/>
<line x1="72.47" y1="379.41" x2="71.73" y2="379.84"/>
<line x1="68.32" y1="381.24" x2="74.33" y2="377.77"/>
<line x1="70.55" y1="383.79" x2="68.32" y2="382.51"/>
<line x1="82.40" y1="378.25" x2="72.77" y2="383.79"/>
<line x1="74.87" y1="380.79" x2="74.13" y2="381.22"/>
<line x1="75.19" y1="381.83" x2="75.94" y2="381.40"/>
<line x1="74.29" y1="381.50" x2="74.13" y2="381.22"/>
<line x1="75.03" y1="381.07" x2="74.87" y2="380.79"/>
<line x1="74.29" y1="381.50" x2="75.03" y2="381.07"/>
<line x1="74.29" y1="381.50" x2="74.29" y2="381.90"/>
<line x1="75.03" y1="381.74" x2="74.65" y2="381.96"/>
<line x1="75.03" y1="381.07" x2="75.03" y2="381.74"/>
<line x1="72.47" y1="379.41" x2="72.64" y2="379.69"/>
<line x1="71.90" y1="380.12" x2="71.73" y2="379.84"/>
<line x1="71.90" y1="380.12" x2="72.64" y2="379.69"/>
<line x1="72.64" y1="379.69" x2="72.64" y2="380.36"/>
<line x1="71.90" y1="380.12" x2="71.90" y2="380.52"/>
<line x1="72.64" y1="380.36" x2="72.26" y2="380.58"/>
<line x1="68.32" y1="382.51" x2="68.32" y2="383.37"/>
<line x1="70.55" y1="384.65" x2="68.32" y2="383.37"/>
<line x1="70.55" y1="383.79" x2="70.55" y2="384.65"/>
<line x1="72.77" y1="383.79" x2="72.77" y2="384.65"/>
<line x1="82.40" y1="379.11" x2="72.77" y2="384.65"/>
<line x1="82.40" y1="378.25" x2="82.40" y2="379.11"/>
<line x1="67.86" y1="381.87" x2="67.86" y2="382.73"/>
<line x1="82.86" y1="377.61" x2="82.86" y2="378.47"/>
<line x1="79.17" y1="375.77" x2="79.22" y2="375.80"/>
<line x1="77.84" y1="375.77" x2="77.78" y2="375.80"/>
<line x1="74.27" y1="377.70" x2="63.38" y2="383.97"/>
<line x1="63.38" y1="387.35" x2="161.16" y2="443.66"/>
<line x1="63.26" y1="389.85" x2="161.16" y2="446.23"/>
<line x1="73.97" y1="103.37" x2="66.08" y2="107.92"/>
<line x1="73.97" y1="103.37" x2="180.64" y2="164.80"/>
<line x1="63.85" y1="106.64" x2="71.75" y2="102.09"/>
<line x1="73.97" y1="103.37" x2="66.08" y2="107.92"/>
<line x1="71.75" y1="102.09" x2="73.97" y2="103.37"/>
<line x1="73.97" y1="377.87" x2="73.97" y2="127.87"/>
<line x1="165.93" y1="49.37" x2="82.33" y2="97.50"/>
<line x1="89.53" y1="380.94" x2="76.19" y2="373.26"/>
<line x1="80.76" y1="96.60" x2="165.56" y2="47.77"/>
<line x1="167.04" y1="48.62" x2="82.24" y2="97.45"/>
<line x1="76.83" y1="379.26" x2="74.60" y2="377.98"/>
<line x1="82.83" y1="377.09" x2="79.05" y2="379.26"/>
<line x1="76.83" y1="379.26" x2="76.83" y2="378.41"/>
<line x1="74.60" y1="377.13" x2="76.83" y2="378.41"/>
<line x1="74.60" y1="377.98" x2="74.60" y2="377.13"/>
<line x1="77.64" y1="374.10" x2="74.60" y2="375.85"/>
<line x1="79.05" y1="378.41" x2="82.09" y2="376.66"/>
<line x1="79.05" y1="379.26" x2="79.05" y2="378.41"/>
<line x1="74.14" y1="377.34" x2="74.14" y2="376.49"/>
<line x1="89.53" y1="386.07" x2="91.68" y2="387.31"/>
<line x1="91.68" y1="387.31" x2="98.35" y2="383.47"/>
<line x1="91.68" y1="138.07" x2="91.68" y2="387.31"/>
<line x1="89.53" y1="386.07" x2="89.53" y2="136.83"/>
<line x1="61.16" y1="391.06" x2="63.38" y2="389.78"/>
<line x1="50.05" y1="384.66" x2="61.16" y2="391.06"/>
<line x1="50.05" y1="114.09" x2="50.05" y2="384.66"/>
<line x1="50.05" y1="114.09" x2="50.05" y2="114.09"/>
<line x1="61.16" y1="120.49" x2="61.16" y2="391.06"/>
<line x1="61.16" y1="120.49" x2="50.05" y2="114.09"/>
<line x1="61.16" y1="120.49" x2="61.16" y2="120.49"/>
<line x1="63.38" y1="389.78" x2="63.38" y2="121.77"/>
<line x1="50.79" y1="99.11" x2="48.57" y2="100.39"/>
<line x1="48.57" y1="100.39" x2="48.57" y2="113.24"/>
<line x1="173.76" y1="172.49" x2="48.57" y2="100.39"/>
<line x1="173.76" y1="185.33" x2="48.57" y2="113.24"/>
<line x1="50.79" y1="99.11" x2="175.98" y2="171.21"/>
<line x1="67.65" y1="104.45" x2="62.86" y2="101.70"/>
<line x1="73.97" y1="127.87" x2="73.97" y2="377.87"/>
<line x1="76.19" y1="374.93" x2="76.19" y2="129.15"/>
<line x1="62.86" y1="101.70" x2="62.86" y2="106.06"/>
<line x1="61.37" y1="88.00" x2="63.60" y2="86.72"/>
<line x1="61.37" y1="100.84" x2="61.37" y2="88.00"/>
<line x1="193.23" y1="163.93" x2="61.37" y2="88.00"/>
<line x1="61.37" y1="100.84" x2="67.65" y2="104.45"/>
<line x1="63.60" y1="86.72" x2="195.46" y2="162.65"/>
</g>
<g class="ptw-wc-annotation" aria-hidden="true">
<line x1="58.54" y1="102" x2="65.46" y2="98" stroke="#bd3a3a" stroke-width="1.5"/>
<line x1="62" y1="100" x2="44.68" y2="90" stroke="#bd3a3a" stroke-width="1.5"/>
<line x1="41.22" y1="92" x2="48.14" y2="88" stroke="#bd3a3a" stroke-width="1.5"/>
<line x1="53.34" y1="95" x2="53.34" y2="66" stroke="#bd3a3a" stroke-width="1.5" stroke-dasharray="3,2"/>
<text class="ptw-wc-rug-label" x="53.34" y="61" fill="#842424" font-size="20" font-weight="700" font-family="'GT America Mono Light',monospace" text-anchor="middle" dominant-baseline="auto">-</text>
</g>
</svg>`,
    'rh-in-iso': `<svg xmlns="http://www.w3.org/2000/svg" width="248" height="500" viewBox="33 0 215 500" overflow="visible">
<rect x="33" width="215" height="500" fill="white"/>
<g transform="scale(-1,1) translate(-248,0)" fill="none" stroke="#1b1d1f" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M 71.70,383.00 A 0.52,0.30 -180.00 1,1 72.58,383.00"/>
<path d="M 71.39,382.97 A 0.75,0.43 -0.00 1,1 71.39,382.97"/>
<path d="M 68.99,381.60 A 0.75,0.43 -0.00 1,1 68.99,381.60"/>
<path d="M 73.54,380.03 A 0.75,0.43 -180.00 0,1 72.47,379.41"/>
<path d="M 71.73,379.84 A 0.75,0.43 -0.00 0,1 72.80,380.45"/>
<path d="M 68.32,381.24 A 1.57,0.90 -0.00 0,1 68.32,382.51"/>
<path d="M 70.55,383.79 A 1.57,0.90 -0.00 0,1 72.77,383.79"/>
<path d="M 82.40,378.25 A 1.57,0.90 -0.00 0,1 82.67,377.18"/>
<path d="M 81.13,377.80 A 2.64,1.52 -0.00 0,1 78.57,376.41"/>
<path d="M 81.38,377.92 A 0.75,0.43 -180.00 0,1 82.45,377.31"/>
<path d="M 74.93,379.55 A 0.75,0.43 -0.00 1,1 74.93,379.55"/>
<path d="M 74.13,381.22 A 0.75,0.43 -180.00 0,1 75.19,381.83"/>
<path d="M 75.94,381.40 A 0.75,0.43 -180.00 0,1 74.87,380.79"/>
<path d="M 69.31,381.62 A 0.52,0.30 -180.00 1,1 70.19,381.62"/>
<path d="M 74.22,381.55 A 0.52,0.30 -180.00 0,1 74.29,381.93"/>
<path d="M 75.03,381.50 A 0.52,0.30 -0.00 0,1 75.79,381.08"/>
<path d="M 72.64,380.12 A 0.52,0.30 -0.00 0,1 73.40,379.71"/>
<path d="M 71.83,380.17 A 0.52,0.30 -0.00 0,1 71.90,380.55"/>
<path d="M 73.10,380.28 A 0.52,0.30 -180.00 0,1 72.64,380.36"/>
<path d="M 75.25,379.58 A 0.52,0.30 -180.00 1,1 76.12,379.58"/>
<path d="M 81.44,377.62 A 0.52,0.30 -0.00 0,1 81.40,377.65"/>
<path d="M 82.10,378.05 A 0.52,0.30 -0.00 0,1 82.32,377.62"/>
<path d="M 75.50,381.66 A 0.52,0.30 -180.00 0,1 75.03,381.74"/>
<path d="M 68.32,382.09 A 1.57,0.90 -0.00 0,1 67.86,382.73"/>
<path d="M 72.77,383.37 A 1.57,0.90 -0.00 0,1 70.55,383.37"/>
<path d="M 82.86,378.47 A 1.57,0.90 -0.00 0,1 82.40,377.83"/>
<path d="M 78.50,376.69 A 0.94,0.54 -180.00 1,1 78.50,376.69"/>
<path d="M 88.44,386.85 A 0.26,0.15 -180.00 1,1 88.44,386.85"/>
<path d="M 76.31,390.86 A 0.26,0.15 -0.00 1,1 76.31,390.86"/>
<path d="M 85.55,388.52 A 0.26,0.15 -0.00 1,1 85.55,388.52"/>
<path d="M 86.04,385.48 A 0.26,0.15 -0.00 1,1 86.04,385.48"/>
<path d="M 79.09,392.46 A 0.26,0.15 -0.00 1,1 79.09,392.46"/>
<path d="M 83.15,387.14 A 0.26,0.15 -0.00 1,1 83.15,387.14"/>
<line x1="161.16" y1="419.77" x2="98.24" y2="383.53"/>
<line x1="174.50" y1="447.97" x2="182.86" y2="443.15"/>
<line x1="182.86" y1="166.08" x2="182.86" y2="443.15"/>
<line x1="182.86" y1="166.08" x2="180.64" y2="164.80"/>
<line x1="174.97" y1="170.63" x2="182.86" y2="166.08"/>
<line x1="172.75" y1="169.35" x2="180.64" y2="164.80"/>
<path d="M 83.06,116.36 A 0.26,0.15 -0.00 1,1 83.06,116.36"/>
<path d="M 69.75,108.09 A 0.25,0.14 -180.00 1,1 69.75,108.09"/>
<path d="M 75.03,107.81 A 0.25,0.14 -0.00 1,1 75.03,107.81"/>
<path d="M 73.33,110.97 A 0.26,0.15 -0.00 1,1 73.33,110.97"/>
<path d="M 72.14,109.47 A 0.25,0.14 -0.00 1,1 72.14,109.47"/>
<path d="M 72.64,106.43 A 0.25,0.14 -180.00 1,1 72.64,106.43"/>
<path d="M 76.22,112.64 A 0.26,0.15 -0.00 1,1 76.22,112.64"/>
<path d="M 78.67,116.56 A 1.57,0.90 -0.00 0,1 80.89,115.28"/>
<line x1="172.75" y1="169.35" x2="180.64" y2="164.80"/>
<line x1="166.02" y1="83.27" x2="166.02" y2="106.34"/>
<line x1="166.02" y1="133.33" x2="166.02" y2="145.70"/>
<line x1="167.23" y1="48.73" x2="166.02" y2="49.42"/>
<line x1="165.56" y1="47.77" x2="167.23" y2="48.73"/>
<line x1="164.35" y1="48.46" x2="165.56" y2="47.77"/>
<line x1="164.35" y1="48.46" x2="162.87" y2="47.61"/>
<line x1="162.87" y1="47.61" x2="162.87" y2="49.31"/>
<line x1="186.57" y1="61.25" x2="166.02" y2="49.42"/>
<line x1="165.09" y1="46.33" x2="162.87" y2="47.61"/>
<line x1="188.79" y1="59.98" x2="165.09" y2="46.33"/>
<path d="M 170.50,58.91 A 0.26,0.15 -0.00 1,1 170.50,58.91"/>
<path d="M 177.46,65.90 A 0.26,0.15 -0.00 1,1 177.46,65.90"/>
<path d="M 169.52,56.16 A 0.26,0.15 -0.00 1,1 169.52,56.16"/>
<path d="M 172.41,54.49 A 0.26,0.15 -0.00 1,1 172.41,54.49"/>
<path d="M 180.24,64.30 A 0.26,0.15 -0.00 1,1 180.24,64.30"/>
<path d="M 168.11,60.29 A 0.26,0.15 -0.00 1,1 168.11,60.29"/>
<path d="M 176.27,65.90 A 0.26,0.15 -0.00 1,1 176.27,65.90"/>
<path d="M 171.00,61.96 A 0.26,0.15 -0.00 1,1 171.00,61.96"/>
<path d="M 171.91,57.54 A 0.26,0.15 -0.00 1,1 171.91,57.54"/>
<path d="M 174.80,55.87 A 0.26,0.15 -180.00 1,1 174.80,55.87"/>
<path d="M 176.86,64.71 A 1.57,0.90 -0.00 1,1 176.86,64.71"/>
<path d="M 173.39,60.58 A 0.26,0.15 -0.00 1,1 173.39,60.58"/>
<path d="M 173.48,64.30 A 0.26,0.15 -0.00 1,1 173.48,64.30"/>
<path d="M 101.55,108.72 A 0.26,0.15 -0.00 1,1 101.91,108.51"/>
<path d="M 98.76,107.12 A 0.26,0.15 -0.00 1,1 99.12,106.91"/>
<line x1="186.57" y1="61.25" x2="165.93" y2="49.37"/>
<line x1="165.56" y1="47.77" x2="167.04" y2="48.62"/>
<line x1="167.04" y1="48.84" x2="167.04" y2="48.62"/>
<path d="M 76.83,379.26 A 1.57,0.90 -0.00 0,1 79.05,379.26"/>
<path d="M 74.14,377.34 A 1.57,0.90 -0.00 0,1 74.60,377.98"/>
<path d="M 78.50,376.75 A 1.01,0.58 -0.00 1,1 78.50,376.75"/>
<path d="M 75.12,376.81 A 0.52,0.30 -0.00 1,1 75.12,376.81"/>
<path d="M 77.91,378.41 A 0.52,0.30 -180.00 1,1 77.91,378.41"/>
<path d="M 74.60,375.85 A 1.57,0.90 -180.00 0,1 74.60,377.13"/>
<path d="M 76.83,378.41 A 1.57,0.90 -0.00 0,1 79.05,378.41"/>
<line x1="161.16" y1="313.33" x2="98.35" y2="349.51"/>
<line x1="161.16" y1="212.27" x2="98.35" y2="248.44"/>
<line x1="192.50" y1="57.84" x2="194.65" y2="59.08"/>
<line x1="192.50" y1="57.84" x2="185.83" y2="61.68"/>
<line x1="187.98" y1="62.92" x2="185.83" y2="61.68"/>
<line x1="194.65" y1="59.08" x2="187.98" y2="62.92"/>
<line x1="194.65" y1="328.01" x2="194.65" y2="175.96"/>
<line x1="194.65" y1="162.18" x2="194.65" y2="59.08"/>
<line x1="187.98" y1="158.34" x2="187.98" y2="62.92"/>
<line x1="194.65" y1="328.01" x2="193.97" y2="328.40"/>
<line x1="185.83" y1="71.86" x2="185.83" y2="157.11"/>
<line x1="98.35" y1="375.76" x2="98.35" y2="383.47"/>
<line x1="98.35" y1="375.76" x2="161.16" y2="339.59"/>
<line x1="161.16" y1="337.11" x2="98.35" y2="373.29"/>
<line x1="98.35" y1="383.47" x2="161.16" y2="347.30"/>
<line x1="98.35" y1="141.91" x2="98.35" y2="383.47"/>
<line x1="187.98" y1="70.63" x2="111.82" y2="114.48"/>
<line x1="187.98" y1="70.63" x2="187.98" y2="62.92"/>
<line x1="187.98" y1="62.92" x2="105.13" y2="110.63"/>
<line x1="187.98" y1="62.92" x2="185.83" y2="61.68"/>
<line x1="102.98" y1="109.39" x2="185.83" y2="61.68"/>
<line x1="172.28" y1="455.05" x2="174.50" y2="453.77"/>
<line x1="161.16" y1="448.65" x2="172.28" y2="455.05"/>
<line x1="172.28" y1="455.05" x2="172.28" y2="184.48"/>
<line x1="172.28" y1="184.48" x2="161.16" y2="178.08"/>
<line x1="161.16" y1="448.65" x2="161.16" y2="178.08"/>
<line x1="174.50" y1="184.91" x2="174.50" y2="453.77"/>
<line x1="172.28" y1="184.48" x2="172.28" y2="184.48"/>
<line x1="161.16" y1="178.08" x2="161.16" y2="178.08"/>
<line x1="173.76" y1="172.49" x2="173.76" y2="185.33"/>
<line x1="175.98" y1="171.21" x2="173.76" y2="172.49"/>
<line x1="175.98" y1="184.05" x2="173.76" y2="185.33"/>
<line x1="175.98" y1="171.21" x2="175.98" y2="184.05"/>
<line x1="98.35" y1="274.70" x2="98.35" y2="282.40"/>
<line x1="161.16" y1="246.23" x2="98.35" y2="282.40"/>
<line x1="161.16" y1="236.05" x2="98.35" y2="272.22"/>
<line x1="98.35" y1="274.70" x2="161.16" y2="238.52"/>
<line x1="195.46" y1="162.65" x2="193.23" y2="163.93"/>
<line x1="193.23" y1="163.93" x2="193.23" y2="176.77"/>
<line x1="193.23" y1="176.77" x2="195.46" y2="175.49"/>
<line x1="195.46" y1="175.49" x2="195.46" y2="162.65"/>
<line x1="182.86" y1="170.80" x2="193.23" y2="176.77"/>
<line x1="191.75" y1="175.92" x2="182.86" y2="170.80"/>
<line x1="193.97" y1="176.35" x2="193.97" y2="446.99"/>
<line x1="191.75" y1="448.27" x2="191.75" y2="175.92"/>
<line x1="193.97" y1="446.99" x2="191.75" y2="448.27"/>
<line x1="191.75" y1="448.27" x2="182.86" y2="443.15"/>
<line x1="145.98" y1="169.34" x2="98.35" y2="196.77"/>
<line x1="161.16" y1="187.80" x2="98.35" y2="223.97"/>
<line x1="98.35" y1="221.40" x2="161.16" y2="185.23"/>
<line x1="185.83" y1="117.75" x2="165.83" y2="106.23"/>
<line x1="165.83" y1="106.23" x2="131.66" y2="125.91"/>
<line x1="101.11" y1="143.50" x2="98.35" y2="145.09"/>
<line x1="185.83" y1="121.92" x2="155.28" y2="139.51"/>
<line x1="124.73" y1="157.10" x2="98.35" y2="172.30"/>
<line x1="98.35" y1="169.73" x2="122.50" y2="155.82"/>
<line x1="153.05" y1="138.23" x2="185.83" y2="119.35"/>
<line x1="161.16" y1="264.09" x2="98.35" y2="300.26"/>
<line x1="161.16" y1="291.29" x2="98.35" y2="327.46"/>
<line x1="98.35" y1="324.89" x2="161.16" y2="288.72"/>
</g>
<polygon points="355.13,501.8 86.84,347.3 86.8,443.7 355.13,598.3" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<text class="ptw-rug-label" x="164.0" y="440.0" fill="#842424" font-size="20" font-weight="700" font-family="'GT America Mono Light',monospace" text-anchor="middle" dominant-baseline="middle">-</text>
<polygon points="54.03,328.4 -107.13,235.6 -107.13,332.0 54.03,424.8" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<g transform="scale(-1,1) translate(-248,0)" fill="none" stroke="#1b1d1f" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<line x1="72.80" y1="380.45" x2="73.54" y2="380.03"/>
<line x1="72.47" y1="379.41" x2="71.73" y2="379.84"/>
<line x1="68.32" y1="381.24" x2="74.33" y2="377.77"/>
<line x1="70.55" y1="383.79" x2="68.32" y2="382.51"/>
<line x1="82.40" y1="378.25" x2="72.77" y2="383.79"/>
<line x1="74.87" y1="380.79" x2="74.13" y2="381.22"/>
<line x1="75.19" y1="381.83" x2="75.94" y2="381.40"/>
<line x1="74.29" y1="381.50" x2="74.13" y2="381.22"/>
<line x1="75.03" y1="381.07" x2="74.87" y2="380.79"/>
<line x1="74.29" y1="381.50" x2="75.03" y2="381.07"/>
<line x1="74.29" y1="381.50" x2="74.29" y2="381.90"/>
<line x1="75.03" y1="381.74" x2="74.65" y2="381.96"/>
<line x1="75.03" y1="381.07" x2="75.03" y2="381.74"/>
<line x1="72.47" y1="379.41" x2="72.64" y2="379.69"/>
<line x1="71.90" y1="380.12" x2="71.73" y2="379.84"/>
<line x1="71.90" y1="380.12" x2="72.64" y2="379.69"/>
<line x1="72.64" y1="379.69" x2="72.64" y2="380.36"/>
<line x1="71.90" y1="380.12" x2="71.90" y2="380.52"/>
<line x1="72.64" y1="380.36" x2="72.26" y2="380.58"/>
<line x1="68.32" y1="382.51" x2="68.32" y2="383.37"/>
<line x1="70.55" y1="384.65" x2="68.32" y2="383.37"/>
<line x1="70.55" y1="383.79" x2="70.55" y2="384.65"/>
<line x1="72.77" y1="383.79" x2="72.77" y2="384.65"/>
<line x1="82.40" y1="379.11" x2="72.77" y2="384.65"/>
<line x1="82.40" y1="378.25" x2="82.40" y2="379.11"/>
<line x1="67.86" y1="381.87" x2="67.86" y2="382.73"/>
<line x1="82.86" y1="377.61" x2="82.86" y2="378.47"/>
<line x1="79.17" y1="375.77" x2="79.22" y2="375.80"/>
<line x1="77.84" y1="375.77" x2="77.78" y2="375.80"/>
<line x1="74.27" y1="377.70" x2="63.38" y2="383.97"/>
<line x1="63.38" y1="387.35" x2="161.16" y2="443.66"/>
<line x1="63.26" y1="389.85" x2="161.16" y2="446.23"/>
<line x1="73.97" y1="103.37" x2="66.08" y2="107.92"/>
<line x1="73.97" y1="103.37" x2="180.64" y2="164.80"/>
<line x1="63.85" y1="106.64" x2="71.75" y2="102.09"/>
<line x1="73.97" y1="103.37" x2="66.08" y2="107.92"/>
<line x1="71.75" y1="102.09" x2="73.97" y2="103.37"/>
<line x1="73.97" y1="377.87" x2="73.97" y2="127.87"/>
<line x1="165.93" y1="49.37" x2="82.33" y2="97.50"/>
<line x1="89.53" y1="380.94" x2="76.19" y2="373.26"/>
<line x1="80.76" y1="96.60" x2="165.56" y2="47.77"/>
<line x1="167.04" y1="48.62" x2="82.24" y2="97.45"/>
<line x1="76.83" y1="379.26" x2="74.60" y2="377.98"/>
<line x1="82.83" y1="377.09" x2="79.05" y2="379.26"/>
<line x1="76.83" y1="379.26" x2="76.83" y2="378.41"/>
<line x1="74.60" y1="377.13" x2="76.83" y2="378.41"/>
<line x1="74.60" y1="377.98" x2="74.60" y2="377.13"/>
<line x1="77.64" y1="374.10" x2="74.60" y2="375.85"/>
<line x1="79.05" y1="378.41" x2="82.09" y2="376.66"/>
<line x1="79.05" y1="379.26" x2="79.05" y2="378.41"/>
<line x1="74.14" y1="377.34" x2="74.14" y2="376.49"/>
<line x1="89.53" y1="386.07" x2="91.68" y2="387.31"/>
<line x1="91.68" y1="387.31" x2="98.35" y2="383.47"/>
<line x1="91.68" y1="138.07" x2="91.68" y2="387.31"/>
<line x1="89.53" y1="386.07" x2="89.53" y2="136.83"/>
<line x1="61.16" y1="391.06" x2="63.38" y2="389.78"/>
<line x1="50.05" y1="384.66" x2="61.16" y2="391.06"/>
<line x1="50.05" y1="114.09" x2="50.05" y2="384.66"/>
<line x1="50.05" y1="114.09" x2="50.05" y2="114.09"/>
<line x1="61.16" y1="120.49" x2="61.16" y2="391.06"/>
<line x1="61.16" y1="120.49" x2="50.05" y2="114.09"/>
<line x1="61.16" y1="120.49" x2="61.16" y2="120.49"/>
<line x1="63.38" y1="389.78" x2="63.38" y2="121.77"/>
<line x1="50.79" y1="99.11" x2="48.57" y2="100.39"/>
<line x1="48.57" y1="100.39" x2="48.57" y2="113.24"/>
<line x1="173.76" y1="172.49" x2="48.57" y2="100.39"/>
<line x1="173.76" y1="185.33" x2="48.57" y2="113.24"/>
<line x1="50.79" y1="99.11" x2="175.98" y2="171.21"/>
<line x1="67.65" y1="104.45" x2="62.86" y2="101.70"/>
<line x1="73.97" y1="127.87" x2="73.97" y2="377.87"/>
<line x1="76.19" y1="374.93" x2="76.19" y2="129.15"/>
<line x1="62.86" y1="101.70" x2="62.86" y2="106.06"/>
<line x1="61.37" y1="88.00" x2="63.60" y2="86.72"/>
<line x1="61.37" y1="100.84" x2="61.37" y2="88.00"/>
<line x1="193.23" y1="163.93" x2="61.37" y2="88.00"/>
<line x1="61.37" y1="100.84" x2="67.65" y2="104.45"/>
<line x1="63.60" y1="86.72" x2="195.46" y2="162.65"/>
</g>
<g class="ptw-wc-annotation" aria-hidden="true">
<line x1="189.46" y1="102" x2="182.54" y2="98" stroke="#bd3a3a" stroke-width="1.5"/>
<line x1="186" y1="100" x2="203.32" y2="90" stroke="#bd3a3a" stroke-width="1.5"/>
<line x1="206.78" y1="92" x2="199.86" y2="88" stroke="#bd3a3a" stroke-width="1.5"/>
<line x1="194.66" y1="95" x2="194.66" y2="66" stroke="#bd3a3a" stroke-width="1.5" stroke-dasharray="3,2"/>
<text class="ptw-wc-rug-label" x="194.66" y="61" fill="#842424" font-size="20" font-weight="700" font-family="'GT America Mono Light',monospace" text-anchor="middle" dominant-baseline="auto">-</text>
</g>
</svg>`,
    'lh-out-iso': `<svg xmlns="http://www.w3.org/2000/svg" width="248" height="500" viewBox="55 -48 240 524" overflow="visible">
<rect x="55" y="-48" width="240" height="524" fill="white"/>
<polygon points="-400,686.5 261.23,305.75 261.23,392.9 -400,773.7" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<text class="ptw-rug-label" x="182.0" y="395.0" fill="#842424" font-size="20" font-weight="700" font-family="'GT America Mono Light',monospace" text-anchor="middle" dominant-baseline="middle">-</text>
<polygon points="288.09,290.27 700,53.04 700,140.19 288.09,377.43" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<g fill="none" stroke="#1b1d1f" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M 88.93,377.25 A 0.34,0.19 -125.13 0,1 88.75,377.25"/>
<path d="M 88.61,377.52 A 0.34,0.19 -180.00 0,1 88.70,377.66"/>
<path d="M 88.94,377.79 A 0.34,0.19 -60.06 0,1 89.18,377.40"/>
<path d="M 88.70,378.31 A 0.15,0.08 -60.06 0,1 88.73,378.38"/>
<path d="M 88.71,378.04 A 0.34,0.19 -179.05 0,1 88.61,378.17"/>
<path d="M 92.80,379.48 A 0.34,0.19 -60.06 0,1 92.58,379.89"/>
<path d="M 92.58,379.89 A 0.34,0.19 -0.00 0,1 93.04,379.89"/>
<path d="M 93.04,379.89 A 0.34,0.17 -119.95 0,1 92.80,379.48"/>
<path d="M 89.35,378.03 A 0.34,0.19 -60.06 0,1 89.58,377.63"/>
<path d="M 89.14,378.17 A 0.34,0.21 -5.79 0,1 89.20,378.01"/>
<path d="M 89.53,377.77 A 0.34,0.27 -0.00 0,1 89.21,378.03"/>
<path d="M 89.53,377.77 A 0.67,0.41 -2.74 0,1 89.33,377.49"/>
<path d="M 93.94,380.17 A 0.15,0.09 -54.27 0,1 93.87,380.17"/>
<path d="M 94.17,380.11 A 0.15,0.08 -0.00 0,1 93.97,380.12"/>
<path d="M 94.17,380.24 A 0.29,0.18 -57.32 0,1 93.96,380.11"/>
<path d="M 93.92,380.18 A 0.15,0.08 -115.65 0,1 93.96,380.11"/>
<path d="M 89.21,378.78 L 89.21,378.74 L 89.20,378.69 L 89.17,378.61 L 89.14,378.58 L 89.11,378.55"/>
<path d="M 89.12,378.61 A 0.15,0.07 -53.02 0,1 89.18,378.61"/>
<path d="M 89.04,378.55 A 0.15,0.08 -60.06 0,1 89.11,378.54"/>
<path d="M 89.11,378.55 A 0.92,0.53 -119.94 0,1 89.01,378.49"/>
<path d="M 88.61,379.67 A 0.34,0.20 -0.99 0,1 88.70,379.81"/>
<path d="M 88.80,379.63 A 0.15,0.08 -60.06 0,1 88.70,379.81"/>
<path d="M 88.80,379.47 A 0.19,0.11 -1.79 0,1 88.80,379.63"/>
<path d="M 88.72,379.47 A 0.15,0.09 -124.30 0,1 88.69,379.55"/>
<path d="M 92.93,380.60 A 0.15,0.08 -115.65 0,1 93.00,380.62"/>
<path d="M 93.04,380.27 A 0.34,0.19 -179.05 0,1 92.58,380.27"/>
<path d="M 92.61,380.61 A 0.15,0.08 -60.06 0,1 92.68,380.60"/>
<path d="M 91.98,380.03 A 0.34,0.21 -5.79 0,1 92.20,379.93"/>
<path d="M 92.40,379.25 A 0.34,0.19 -60.06 0,1 92.17,379.65"/>
<path d="M 92.40,379.25 A 0.67,0.41 -2.74 0,1 91.92,379.15"/>
<path d="M 93.96,381.53 A 0.29,0.18 -57.32 0,1 94.17,381.18"/>
<path d="M 93.97,381.18 A 0.15,0.08 -180.00 0,1 94.17,381.18"/>
<path d="M 93.87,381.10 A 0.15,0.09 -54.27 0,1 93.74,381.26"/>
<path d="M 93.96,381.53 A 0.15,0.08 -119.11 0,1 93.86,381.35"/>
<path d="M 93.37,380.83 A 0.15,0.08 -60.06 0,1 93.26,381.01"/>
<path d="M 92.98,380.63 A 0.72,0.42 -119.94 0,1 93.37,381.07"/>
<path d="M 93.88,380.66 A 0.57,0.33 -119.94 0,1 93.73,380.22"/>
<path d="M 88.70,384.92 A 0.15,0.08 -60.06 0,1 88.73,384.99"/>
<path d="M 88.71,384.65 A 0.34,0.19 -179.05 0,1 88.61,384.78"/>
<path d="M 89.11,380.03 L 89.13,380.04 L 89.15,380.04 L 89.18,380.05 L 89.19,380.06 L 89.20,380.07 L 89.21,380.07 L 89.21,380.07"/>
<path d="M 89.21,379.85 L 89.20,379.85 L 89.20,379.85 L 89.19,379.85"/>
<path d="M 89.25,380.09 A 0.15,0.10 -68.26 0,1 89.31,379.89"/>
<path d="M 89.11,379.81 A 0.15,0.08 -60.06 0,1 89.01,379.98"/>
<path d="M 89.01,379.98 A 0.92,0.53 -119.94 0,1 89.11,380.03"/>
<path d="M 89.65,379.58 A 0.77,0.45 -119.94 0,1 89.66,379.49"/>
<path d="M 92.17,380.31 A 0.15,0.10 -68.26 0,1 92.22,380.39"/>
<path d="M 92.17,380.31 L 92.15,380.31 L 92.13,380.31 L 92.08,380.31 L 92.04,380.31 L 91.98,380.33 L 91.96,380.34 L 91.94,380.35"/>
<path d="M 92.58,382.04 A 0.34,0.20 -0.99 0,1 93.04,382.04"/>
<path d="M 92.84,381.91 A 0.15,0.08 -119.11 0,1 92.94,382.09"/>
<path d="M 92.93,381.69 A 0.19,0.11 -1.79 0,1 92.67,381.71"/>
<path d="M 92.68,382.10 A 0.15,0.08 -60.06 0,1 92.79,381.92"/>
<path d="M 93.94,386.78 A 0.15,0.09 -54.27 0,1 93.87,386.78"/>
<path d="M 94.17,386.72 A 0.15,0.08 -0.00 0,1 93.97,386.73"/>
<path d="M 94.17,386.85 A 0.29,0.18 -57.32 0,1 93.96,386.72"/>
<path d="M 93.92,386.79 A 0.15,0.08 -115.65 0,1 93.96,386.72"/>
<path d="M 89.21,385.39 L 89.21,385.35 L 89.20,385.30 L 89.17,385.22 L 89.14,385.19 L 89.11,385.16"/>
<path d="M 89.12,385.22 A 0.15,0.07 -53.02 0,1 89.18,385.22"/>
<path d="M 89.04,385.16 A 0.15,0.08 -60.06 0,1 89.11,385.16"/>
<path d="M 89.11,385.16 A 0.92,0.53 -119.94 0,1 89.01,385.10"/>
<path d="M 88.61,386.28 A 0.34,0.20 -0.99 0,1 88.70,386.42"/>
<path d="M 88.80,386.24 A 0.15,0.08 -60.06 0,1 88.70,386.42"/>
<path d="M 88.80,386.08 A 0.19,0.11 -1.79 0,1 88.80,386.24"/>
<path d="M 88.72,386.08 A 0.15,0.09 -124.30 0,1 88.69,386.16"/>
<path d="M 92.93,387.21 A 0.15,0.08 -115.65 0,1 93.00,387.23"/>
<path d="M 93.04,386.88 A 0.34,0.19 -179.05 0,1 92.58,386.88"/>
<path d="M 92.61,387.22 A 0.15,0.08 -60.06 0,1 92.68,387.21"/>
<path d="M 92.17,381.80 A 1.24,1.24 0 0,1 91.94,381.64"/>
<path d="M 92.29,381.63 A 0.15,0.07 -53.02 0,1 92.17,381.80"/>
<path d="M 92.29,381.63 A 0.89,0.89 0 0,1 91.99,381.49"/>
<path d="M 92.37,381.68 A 0.15,0.08 -60.06 0,1 92.27,381.86"/>
<path d="M 92.17,381.80 A 0.92,0.53 -119.94 0,1 92.27,381.86"/>
<path d="M 92.91,381.37 A 0.77,0.45 -119.94 0,1 92.91,381.27"/>
<path d="M 91.94,380.35 A 1.28,0.74 -180.00 0,1 91.94,381.64"/>
<path d="M 91.99,381.49 L 91.93,381.48 L 91.88,381.46 L 91.71,381.40 L 91.61,381.34 L 91.47,381.22 L 91.43,381.16 L 91.39,381.06 L 91.39,381.02 L 91.39,380.98 L 91.39,380.94 L 91.39,380.90 L 91.43,380.79 L 91.47,380.73 L 91.60,380.60 L 91.68,380.55 L 91.78,380.49"/>
<path d="M 93.96,388.14 A 0.29,0.18 -57.32 0,1 94.17,387.79"/>
<path d="M 93.97,387.79 A 0.15,0.08 -180.00 0,1 94.17,387.79"/>
<path d="M 93.87,387.71 A 0.15,0.09 -54.27 0,1 93.74,387.87"/>
<path d="M 93.96,388.14 A 0.15,0.08 -119.11 0,1 93.86,387.96"/>
<path d="M 93.37,387.68 A 0.15,0.08 -60.06 0,1 93.47,387.50"/>
<path d="M 93.88,387.27 A 0.57,0.33 -119.94 0,1 93.73,386.83"/>
<path d="M 92.98,387.24 A 0.72,0.42 -119.94 0,1 93.37,387.68"/>
<path d="M 88.77,387.22 A 0.34,0.19 -60.06 0,1 88.94,387.21"/>
<path d="M 88.70,386.80 A 0.34,0.19 -180.00 0,1 88.61,386.93"/>
<path d="M 89.11,386.64 L 89.13,386.65 L 89.15,386.65 L 89.18,386.67 L 89.19,386.67 L 89.20,386.68 L 89.21,386.68 L 89.21,386.68"/>
<path d="M 89.21,386.46 L 89.20,386.46 L 89.20,386.46 L 89.19,386.46"/>
<path d="M 89.25,386.70 A 0.15,0.10 -68.26 0,1 89.31,386.50"/>
<path d="M 89.11,386.42 A 0.15,0.08 -60.06 0,1 89.01,386.59"/>
<path d="M 89.01,386.59 A 0.92,0.53 -119.94 0,1 89.11,386.64"/>
<path d="M 89.65,386.19 A 0.77,0.45 -119.94 0,1 89.66,386.10"/>
<path d="M 92.17,386.92 A 0.15,0.10 -68.26 0,1 92.22,387.00"/>
<path d="M 92.17,386.92 L 92.15,386.92 L 92.13,386.92 L 92.08,386.92 L 92.04,386.92 L 91.98,386.94 L 91.96,386.95 L 91.94,386.97"/>
<path d="M 92.93,388.30 A 0.19,0.11 -1.79 0,1 92.67,388.32"/>
<path d="M 92.68,388.71 A 0.15,0.08 -60.06 0,1 92.79,388.53"/>
<path d="M 92.58,388.65 A 0.34,0.20 -0.99 0,1 93.04,388.65"/>
<path d="M 92.84,388.52 A 0.15,0.08 -119.11 0,1 92.94,388.70"/>
<path d="M 92.96,389.47 A 0.34,0.19 -114.75 0,1 93.04,389.31"/>
<path d="M 93.04,389.02 A 0.34,0.19 -0.00 0,1 92.58,389.03"/>
<path d="M 92.58,389.30 A 0.34,0.19 -60.06 0,1 92.65,389.45"/>
<path d="M 89.21,387.43 A 0.34,0.17 -175.29 0,1 89.11,387.30"/>
<path d="M 89.18,387.46 A 0.34,0.19 -60.06 0,1 89.35,387.44"/>
<path d="M 92.29,388.24 A 0.89,0.89 0 0,1 91.99,388.10"/>
<path d="M 92.17,388.41 A 1.24,1.24 0 0,1 91.94,388.25"/>
<path d="M 92.29,388.24 A 0.15,0.07 -53.02 0,1 92.17,388.41"/>
<path d="M 91.94,386.97 A 1.28,0.74 -180.00 0,1 91.94,388.25"/>
<path d="M 91.99,388.10 L 91.93,388.09 L 91.88,388.07 L 91.71,388.01 L 91.61,387.95 L 91.47,387.83 L 91.43,387.77 L 91.39,387.67 L 91.39,387.63 L 91.39,387.59 L 91.39,387.55 L 91.39,387.51 L 91.43,387.40 L 91.47,387.34 L 91.60,387.21 L 91.68,387.16 L 91.78,387.10"/>
<path d="M 92.17,389.07 A 0.34,0.19 -60.06 0,1 92.24,389.22"/>
<path d="M 92.17,389.07 A 0.34,0.17 -175.29 0,1 91.94,389.00"/>
<path d="M 91.09,386.98 A 1.81,1.00 -177.97 0,1 89.21,387.89"/>
<path d="M 92.37,388.29 A 0.15,0.08 -60.06 0,1 92.27,388.47"/>
<path d="M 92.17,388.41 A 0.92,0.53 -119.94 0,1 92.27,388.47"/>
<path d="M 92.91,387.98 A 0.77,0.45 -119.94 0,1 92.91,387.88"/>
<path d="M 94.38,388.81 A 0.34,0.19 -119.94 0,1 94.46,388.65"/>
<path d="M 92.30,387.04 A 0.15,0.08 -60.06 0,1 92.37,387.03"/>
<path d="M 92.27,386.97 A 0.92,0.53 -119.94 0,1 92.17,386.92"/>
<path d="M 92.30,380.43 A 0.15,0.08 -60.06 0,1 92.37,380.42"/>
<path d="M 92.27,380.36 A 0.92,0.53 -119.94 0,1 92.17,380.31"/>
<path d="M 89.21,378.59 A 0.34,0.27 -0.00 0,1 89.53,378.86"/>
<path d="M 90.92,377.39 A 1.47,0.89 -2.74 0,1 89.53,378.38"/>
<path d="M 89.21,378.59 A 1.81,1.09 -2.21 0,1 91.02,379.63"/>
<path d="M 94.46,379.08 A 0.34,0.19 -119.94 0,1 94.22,378.67"/>
<path d="M 90.20,376.36 A 0.34,0.19 -119.94 0,1 90.03,376.34"/>
<path d="M 93.29,380.52 A 0.36,0.21 -119.94 1,1 93.63,380.28"/>
<path d="M 93.29,387.13 A 0.36,0.21 -119.94 1,1 93.63,386.89"/>
<path d="M 88.73,378.38 L 88.72,378.37 L 88.71,378.36 L 88.69,378.34 L 88.67,378.33 L 88.65,378.31 L 88.64,378.29 L 88.63,378.26 L 88.62,378.25 L 88.61,378.21 L 88.61,378.19 L 88.61,378.17"/>
<path d="M 93.92,380.18 L 93.93,380.18 L 93.93,380.18 L 93.94,380.17 L 93.94,380.17 L 93.95,380.17 L 93.95,380.17 L 93.95,380.17"/>
<path d="M 89.21,378.78 L 89.21,378.77 L 89.21,378.76 L 89.20,378.73 L 89.20,378.72 L 89.19,378.69 L 89.18,378.68 L 89.17,378.65 L 89.16,378.64 L 89.14,378.62 L 89.14,378.62 L 89.12,378.61"/>
<path d="M 89.12,378.61 A 0.81,0.81 0 0,1 89.04,378.55"/>
<path d="M 88.72,379.47 L 88.71,379.48 L 88.69,379.49 L 88.67,379.51 L 88.66,379.52 L 88.64,379.54 L 88.64,379.56 L 88.62,379.59 L 88.62,379.60 L 88.61,379.63 L 88.61,379.65 L 88.61,379.67"/>
<path d="M 93.00,380.62 L 92.98,380.63 L 92.95,380.64 L 92.90,380.65 L 92.87,380.66 L 92.82,380.66 L 92.79,380.66 L 92.74,380.65 L 92.71,380.65 L 92.66,380.63 L 92.63,380.62 L 92.61,380.61"/>
<path d="M 88.73,384.99 L 88.72,384.98 L 88.71,384.97 L 88.69,384.95 L 88.67,384.94 L 88.65,384.92 L 88.64,384.90 L 88.63,384.87 L 88.62,384.86 L 88.61,384.82 L 88.61,384.80 L 88.61,384.78"/>
<path d="M 92.22,380.39 L 92.21,380.39 L 92.20,380.38 L 92.18,380.38 L 92.16,380.38 L 92.13,380.38 L 92.12,380.38 L 92.08,380.38 L 92.06,380.39 L 92.02,380.40 L 92.00,380.40 L 91.98,380.41"/>
<path d="M 93.92,386.79 L 93.93,386.79 L 93.93,386.79 L 93.94,386.78 L 93.94,386.78 L 93.95,386.78 L 93.95,386.78 L 93.95,386.78"/>
<path d="M 89.21,385.39 L 89.21,385.38 L 89.21,385.37 L 89.20,385.34 L 89.20,385.33 L 89.19,385.30 L 89.18,385.29 L 89.17,385.26 L 89.16,385.25 L 89.14,385.23 L 89.14,385.23 L 89.12,385.22"/>
<path d="M 89.12,385.22 A 0.81,0.81 0 0,1 89.04,385.16"/>
<path d="M 88.72,386.08 L 88.71,386.09 L 88.69,386.10 L 88.67,386.12 L 88.66,386.13 L 88.64,386.15 L 88.64,386.17 L 88.62,386.20 L 88.62,386.21 L 88.61,386.24 L 88.61,386.26 L 88.61,386.28"/>
<path d="M 93.00,387.23 L 92.98,387.24 L 92.95,387.25 L 92.90,387.26 L 92.87,387.27 L 92.82,387.27 L 92.79,387.27 L 92.74,387.26 L 92.71,387.26 L 92.66,387.24 L 92.63,387.23 L 92.61,387.22"/>
<path d="M 92.22,387.00 L 92.21,387.00 L 92.20,386.99 L 92.18,386.99 L 92.16,386.99 L 92.13,386.99 L 92.12,386.99 L 92.08,386.99 L 92.06,387.00 L 92.02,387.01 L 92.00,387.01 L 91.98,387.02"/>
<path d="M 89.21,387.48 L 89.20,387.47 L 89.19,387.46 L 89.18,387.46"/>
<path d="M 92.24,389.22 L 92.23,389.21 L 92.21,389.21 L 92.18,389.19 L 92.16,389.18 L 92.12,389.17 L 92.10,389.17 L 92.05,389.16 L 92.03,389.15 L 91.98,389.15 L 91.95,389.14 L 91.92,389.14"/>
<path d="M 91.01,389.12 L 90.83,389.11 L 90.66,389.09 L 90.33,389.02 L 90.17,388.97 L 89.87,388.85 L 89.74,388.77 L 89.50,388.59 L 89.40,388.48 L 89.26,388.24 L 89.22,388.11 L 89.21,387.96 L 89.21,387.95 L 89.21,387.93"/>
<path d="M 92.30,387.04 A 0.62,0.62 0 0,1 92.22,387.00"/>
<path d="M 92.30,380.43 A 0.62,0.62 0 0,1 92.22,380.39"/>
<path d="M 92.04,390.00 L 91.84,390.74 L 91.65,390.43"/>
<path d="M 91.91,390.21 A 0.91,0.53 -180.00 0,1 90.12,390.21"/>
<path d="M 91.65,389.49 A 1.03,0.59 -180.00 0,1 89.99,389.96"/>
<path d="M 91.91,390.41 L 91.89,390.48 L 91.86,390.54 L 91.77,390.66 L 91.71,390.71 L 91.58,390.81 L 91.51,390.85 L 91.33,390.92 L 91.24,390.94 L 91.04,390.96 L 90.93,390.96 L 90.73,390.93 L 90.63,390.90 L 90.44,390.81 L 90.35,390.75 L 90.21,390.60 L 90.16,390.52 L 90.12,390.42 L 90.12,390.41 L 90.12,390.41"/>
<line x1="261.23" y1="369.01" x2="204.14" y2="336.13"/>
<line x1="185.56" y1="349.32" x2="261.23" y2="392.90"/>
<line x1="261.23" y1="395.47" x2="261.23" y2="392.90"/>
<line x1="183.32" y1="350.60" x2="261.23" y2="395.47"/>
<line x1="263.45" y1="396.74" x2="276.97" y2="388.96"/>
<line x1="263.45" y1="396.74" x2="261.23" y2="395.47"/>
<line x1="276.97" y1="111.89" x2="276.97" y2="388.96"/>
<line x1="276.97" y1="111.89" x2="274.74" y2="110.61"/>
<line x1="270.09" y1="115.85" x2="276.97" y2="111.89"/>
<line x1="268.28" y1="114.33" x2="274.74" y2="110.61"/>
<line x1="263.45" y1="396.74" x2="263.45" y2="126.96"/>
<line x1="261.23" y1="395.47" x2="261.23" y2="125.68"/>
<path d="M 177.17,62.16 A 0.26,0.15 -180.00 0,1 177.43,62.01"/>
<path d="M 163.85,53.90 A 0.25,0.14 -0.00 1,1 163.85,53.90"/>
<path d="M 169.14,53.62 A 0.25,0.14 -0.00 1,1 169.14,53.62"/>
<path d="M 166.25,55.28 A 0.25,0.14 -180.00 1,1 166.25,55.28"/>
<path d="M 166.74,52.24 A 0.25,0.14 -180.00 1,1 166.74,52.24"/>
<line x1="168.07" y1="49.18" x2="161.61" y2="52.90"/>
<line x1="168.07" y1="49.18" x2="274.74" y2="110.61"/>
<line x1="268.28" y1="114.33" x2="274.74" y2="110.61"/>
<line x1="159.39" y1="51.62" x2="165.85" y2="47.90"/>
<line x1="168.07" y1="49.18" x2="161.61" y2="52.90"/>
<line x1="165.85" y1="47.90" x2="168.07" y2="49.18"/>
<path d="M 101.69,117.56 A 0.26,0.15 -0.00 1,1 101.69,117.56"/>
<path d="M 94.73,110.57 A 0.26,0.15 -0.00 1,1 94.73,110.57"/>
<path d="M 102.67,120.31 A 0.26,0.15 -0.00 1,1 102.67,120.31"/>
<path d="M 99.78,121.98 A 0.26,0.15 -180.00 1,1 99.78,121.98"/>
<path d="M 91.95,112.18 A 0.26,0.15 -0.00 1,1 91.95,112.18"/>
<path d="M 104.08,116.18 A 0.26,0.15 -0.00 1,1 104.08,116.18"/>
<path d="M 95.92,110.57 A 0.26,0.15 -0.00 1,1 95.92,110.57"/>
<path d="M 101.19,114.52 A 0.26,0.15 -180.00 1,1 101.19,114.52"/>
<path d="M 100.28,118.94 A 0.26,0.15 -0.00 1,1 100.28,118.94"/>
<path d="M 97.39,120.60 A 0.26,0.15 -0.00 1,1 97.39,120.60"/>
<path d="M 95.33,113.27 A 1.57,0.90 -0.00 1,1 95.33,113.27"/>
<path d="M 98.80,115.89 A 0.26,0.15 -0.00 1,1 98.80,115.89"/>
<path d="M 98.70,112.18 A 0.26,0.15 -0.00 1,1 98.70,112.18"/>
<line x1="200.44" y1="90.67" x2="200.44" y2="340.75"/>
<line x1="200.44" y1="340.75" x2="201.92" y2="341.60"/>
<line x1="201.92" y1="341.60" x2="201.92" y2="91.52"/>
<line x1="204.14" y1="340.33" x2="204.14" y2="92.80"/>
<line x1="204.14" y1="340.33" x2="201.92" y2="341.60"/>
<line x1="155.26" y1="64.66" x2="155.26" y2="64.66"/>
<line x1="144.15" y1="58.26" x2="144.15" y2="58.26"/>
<line x1="155.26" y1="64.66" x2="144.15" y2="58.26"/>
<line x1="155.26" y1="64.66" x2="155.26" y2="70.62"/>
<line x1="157.49" y1="69.34" x2="157.49" y2="65.94"/>
<line x1="144.15" y1="58.26" x2="144.15" y2="77.02"/>
<line x1="144.89" y1="43.28" x2="142.67" y2="44.56"/>
<line x1="142.67" y1="44.56" x2="142.67" y2="57.41"/>
<line x1="267.86" y1="116.65" x2="142.67" y2="44.56"/>
<line x1="267.86" y1="129.50" x2="162.65" y2="68.91"/>
<line x1="160.44" y1="67.64" x2="142.67" y2="57.41"/>
<line x1="267.86" y1="116.65" x2="267.86" y2="129.50"/>
<line x1="270.09" y1="115.37" x2="267.86" y2="116.65"/>
<line x1="270.09" y1="128.22" x2="267.86" y2="129.50"/>
<line x1="270.09" y1="115.37" x2="270.09" y2="128.22"/>
<line x1="144.89" y1="43.28" x2="270.09" y2="115.37"/>
<line x1="161.75" y1="50.26" x2="156.96" y2="47.50"/>
<line x1="156.96" y1="47.50" x2="156.96" y2="50.23"/>
<line x1="155.48" y1="33.80" x2="157.70" y2="32.52"/>
<line x1="155.48" y1="46.65" x2="155.48" y2="33.80"/>
<line x1="287.34" y1="109.74" x2="155.48" y2="33.80"/>
<line x1="155.48" y1="46.65" x2="161.75" y2="50.26"/>
<line x1="157.70" y1="32.52" x2="289.56" y2="108.46"/>
<line x1="285.86" y1="121.73" x2="276.97" y2="116.61"/>
<line x1="285.86" y1="394.08" x2="276.97" y2="388.96"/>
<line x1="285.88" y1="394.00" x2="285.88" y2="121.72"/>
<line x1="288.09" y1="122.15" x2="288.09" y2="392.86"/>
<line x1="287.35" y1="109.72" x2="287.35" y2="122.58"/>
<line x1="289.53" y1="121.28" x2="289.53" y2="108.45"/>
<line x1="288.09" y1="392.86" x2="285.88" y2="394.00"/>
<line x1="289.53" y1="108.45" x2="287.35" y2="109.72"/>
<line x1="287.35" y1="122.58" x2="289.53" y2="121.28"/>
<line x1="88.70" y1="378.31" x2="88.70" y2="377.66"/>
<line x1="89.11" y1="377.89" x2="89.11" y2="378.55"/>
<line x1="89.01" y1="378.49" x2="88.70" y2="378.31"/>
<line x1="92.58" y1="379.89" x2="92.58" y2="380.54"/>
<line x1="93.04" y1="380.55" x2="93.04" y2="379.89"/>
<line x1="94.17" y1="381.18" x2="94.17" y2="380.24"/>
<line x1="93.97" y1="380.24" x2="93.97" y2="381.18"/>
<line x1="93.37" y1="381.07" x2="93.86" y2="381.35"/>
<line x1="93.97" y1="381.18" x2="93.47" y2="380.89"/>
<line x1="93.89" y1="380.19" x2="93.97" y2="380.24"/>
<line x1="88.70" y1="384.92" x2="88.70" y2="379.81"/>
<line x1="88.70" y1="379.81" x2="89.01" y2="379.98"/>
<line x1="89.11" y1="379.81" x2="88.80" y2="379.63"/>
<line x1="91.94" y1="379.61" x2="91.94" y2="380.35"/>
<line x1="92.17" y1="380.31" x2="92.17" y2="379.65"/>
<line x1="93.04" y1="382.04" x2="93.96" y2="381.53"/>
<line x1="93.86" y1="381.35" x2="92.94" y2="381.86"/>
<line x1="89.11" y1="380.03" x2="89.11" y2="385.16"/>
<line x1="89.01" y1="385.10" x2="88.70" y2="384.92"/>
<line x1="92.58" y1="382.04" x2="92.58" y2="387.15"/>
<line x1="93.04" y1="387.16" x2="93.04" y2="382.04"/>
<line x1="92.27" y1="381.86" x2="92.58" y2="382.04"/>
<line x1="92.68" y1="381.86" x2="92.37" y2="381.68"/>
<line x1="94.17" y1="387.79" x2="94.17" y2="386.85"/>
<line x1="93.97" y1="386.85" x2="93.97" y2="387.79"/>
<line x1="93.89" y1="386.80" x2="93.97" y2="386.85"/>
<line x1="93.97" y1="387.79" x2="93.47" y2="387.50"/>
<line x1="93.37" y1="387.68" x2="93.86" y2="387.96"/>
<line x1="88.70" y1="387.07" x2="88.70" y2="386.42"/>
<line x1="88.70" y1="386.42" x2="89.01" y2="386.59"/>
<line x1="89.11" y1="386.42" x2="88.80" y2="386.24"/>
<line x1="91.94" y1="381.64" x2="91.94" y2="386.97"/>
<line x1="92.17" y1="386.92" x2="92.17" y2="381.80"/>
<line x1="92.58" y1="388.65" x2="92.58" y2="389.30"/>
<line x1="93.04" y1="389.31" x2="93.04" y2="388.65"/>
<line x1="89.11" y1="386.64" x2="89.11" y2="387.30"/>
<line x1="89.11" y1="387.30" x2="88.70" y2="387.07"/>
<line x1="91.94" y1="388.25" x2="91.94" y2="389.00"/>
<line x1="92.17" y1="389.07" x2="92.17" y2="388.41"/>
<line x1="91.94" y1="389.00" x2="91.02" y2="388.97"/>
<line x1="92.58" y1="389.30" x2="92.17" y2="389.07"/>
<line x1="92.27" y1="388.47" x2="92.58" y2="388.65"/>
<line x1="92.68" y1="388.47" x2="92.37" y2="388.29"/>
<line x1="94.46" y1="388.65" x2="93.04" y2="389.31"/>
<line x1="93.04" y1="388.65" x2="93.96" y2="388.14"/>
<line x1="93.86" y1="387.96" x2="92.94" y2="388.47"/>
<line x1="92.58" y1="387.15" x2="92.27" y2="386.97"/>
<line x1="93.96" y1="386.72" x2="93.04" y2="387.16"/>
<line x1="88.80" y1="386.09" x2="89.21" y2="385.80"/>
<line x1="92.58" y1="380.54" x2="92.27" y2="380.36"/>
<line x1="92.80" y1="379.48" x2="92.40" y2="379.25"/>
<line x1="92.17" y1="379.65" x2="92.58" y2="379.89"/>
<line x1="89.53" y1="378.33" x2="89.53" y2="377.77"/>
<line x1="89.21" y1="378.03" x2="89.21" y2="378.59"/>
<line x1="91.92" y1="379.15" x2="91.00" y2="379.17"/>
<line x1="91.02" y1="379.63" x2="91.94" y2="379.61"/>
<line x1="93.96" y1="380.11" x2="93.04" y2="380.55"/>
<line x1="88.80" y1="379.48" x2="89.21" y2="379.19"/>
<line x1="89.33" y1="377.49" x2="88.93" y2="377.25"/>
<line x1="88.70" y1="377.66" x2="89.11" y2="377.89"/>
<line x1="94.22" y1="378.67" x2="92.80" y2="379.48"/>
<line x1="93.04" y1="379.89" x2="94.46" y2="379.08"/>
<line x1="88.93" y1="377.25" x2="90.20" y2="376.36"/>
<line x1="89.21" y1="379.75" x2="89.11" y2="379.81"/>
<line x1="91.02" y1="379.63" x2="91.02" y2="388.97"/>
<line x1="89.21" y1="387.93" x2="89.21" y2="378.59"/>
<line x1="89.21" y1="386.36" x2="89.11" y2="386.42"/>
<line x1="94.22" y1="378.67" x2="90.20" y2="376.36"/>
<line x1="94.46" y1="388.65" x2="94.46" y2="379.08"/>
<line x1="93.37" y1="387.68" x2="92.37" y2="388.29"/>
<line x1="93.37" y1="381.07" x2="92.37" y2="381.68"/>
<line x1="88.61" y1="378.17" x2="88.61" y2="377.52"/>
<line x1="88.73" y1="378.38" x2="89.04" y2="378.55"/>
<line x1="88.61" y1="384.78" x2="88.61" y2="379.67"/>
<line x1="88.73" y1="384.99" x2="89.04" y2="385.16"/>
<line x1="88.61" y1="386.93" x2="88.61" y2="386.28"/>
<line x1="89.18" y1="387.46" x2="88.77" y2="387.22"/>
<line x1="91.92" y1="389.14" x2="91.01" y2="389.12"/>
<line x1="92.65" y1="389.45" x2="92.24" y2="389.22"/>
<line x1="94.38" y1="388.81" x2="92.96" y2="389.47"/>
<line x1="92.30" y1="387.04" x2="92.61" y2="387.22"/>
<line x1="93.00" y1="387.23" x2="93.92" y2="386.79"/>
<line x1="88.72" y1="386.08" x2="89.21" y2="385.75"/>
<line x1="92.30" y1="380.43" x2="92.61" y2="380.61"/>
<line x1="93.00" y1="380.62" x2="93.92" y2="380.18"/>
<line x1="88.72" y1="379.47" x2="89.21" y2="379.14"/>
<line x1="90.03" y1="376.34" x2="88.75" y2="377.25"/>
<line x1="91.65" y1="390.43" x2="91.65" y2="389.14"/>
<line x1="92.04" y1="389.16" x2="92.04" y2="390.00"/>
<line x1="90.12" y1="390.41" x2="90.01" y2="390.07"/>
<line x1="91.91" y1="390.41" x2="91.98" y2="390.21"/>
<line x1="89.99" y1="388.90" x2="89.99" y2="389.96"/>
<line x1="92.04" y1="389.16" x2="92.04" y2="389.96"/>
<line x1="106.17" y1="126.75" x2="106.17" y2="126.97"/>
<line x1="104.96" y1="127.44" x2="106.17" y2="126.75"/>
<line x1="106.63" y1="128.40" x2="104.96" y2="127.44"/>
<line x1="107.84" y1="127.71" x2="106.63" y2="128.40"/>
<line x1="107.84" y1="127.71" x2="109.32" y2="128.56"/>
<line x1="109.32" y1="128.56" x2="109.32" y2="394.93"/>
<line x1="84.87" y1="114.49" x2="106.17" y2="126.75"/>
<line x1="107.10" y1="396.21" x2="109.32" y2="394.93"/>
<line x1="82.65" y1="382.13" x2="89.08" y2="385.83"/>
<line x1="94.31" y1="388.84" x2="107.10" y2="396.21"/>
<line x1="107.10" y1="129.84" x2="107.10" y2="396.21"/>
<line x1="107.10" y1="129.84" x2="109.32" y2="128.56"/>
<line x1="82.65" y1="115.77" x2="84.87" y2="114.49"/>
<line x1="82.65" y1="115.77" x2="107.10" y2="129.84"/>
<line x1="82.65" y1="382.13" x2="82.65" y2="115.77"/>
<line x1="106.26" y1="126.80" x2="184.72" y2="81.62"/>
<line x1="106.26" y1="126.91" x2="106.26" y2="126.80"/>
<line x1="84.87" y1="114.49" x2="106.26" y2="126.80"/>
<line x1="84.87" y1="114.49" x2="163.33" y2="69.30"/>
<line x1="186.30" y1="82.53" x2="106.63" y2="128.40"/>
<line x1="105.15" y1="127.55" x2="184.82" y2="81.67"/>
<line x1="106.63" y1="128.40" x2="105.15" y2="127.55"/>
<line x1="109.32" y1="393.22" x2="200.44" y2="340.75"/>
<line x1="79.69" y1="116.62" x2="77.54" y2="115.38"/>
<line x1="79.69" y1="116.62" x2="86.36" y2="112.78"/>
<line x1="84.21" y1="111.54" x2="86.36" y2="112.78"/>
<line x1="77.54" y1="115.38" x2="84.21" y2="111.54"/>
<line x1="77.54" y1="384.31" x2="77.54" y2="115.38"/>
<line x1="79.69" y1="385.55" x2="77.54" y2="384.31"/>
<line x1="79.69" y1="385.55" x2="84.14" y2="382.99"/>
<line x1="79.69" y1="116.62" x2="79.69" y2="385.55"/>
<line x1="86.36" y1="112.78" x2="86.36" y2="113.63"/>
<line x1="84.21" y1="111.54" x2="160.50" y2="67.60"/>
<line x1="86.36" y1="112.78" x2="86.36" y2="113.63"/>
<line x1="84.21" y1="111.54" x2="86.36" y2="112.78"/>
<line x1="162.65" y1="68.84" x2="86.36" y2="112.78"/>
<line x1="76.80" y1="113.25" x2="79.02" y2="114.53"/>
<line x1="65.69" y1="119.64" x2="76.80" y2="113.25"/>
<line x1="67.91" y1="120.92" x2="65.69" y2="119.64"/>
<line x1="79.02" y1="114.53" x2="67.91" y2="120.92"/>
<line x1="67.91" y1="120.92" x2="67.91" y2="393.20"/>
<line x1="67.91" y1="393.20" x2="79.02" y2="386.80"/>
<line x1="79.02" y1="385.17" x2="79.02" y2="386.80"/>
<line x1="65.69" y1="391.92" x2="65.69" y2="119.64"/>
<line x1="67.91" y1="393.20" x2="65.69" y2="391.92"/>
</g>
</svg>`,
    'rh-out-iso': `<svg xmlns="http://www.w3.org/2000/svg" width="248" height="500" viewBox="-15 -48 240 524" overflow="visible">
<rect x="-15" y="-48" width="240" height="524" fill="white"/>
<polygon points="680,686.5 18.77,305.75 18.77,392.9 680,773.7" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<text class="ptw-rug-label" x="98.0" y="395.0" fill="#842424" font-size="20" font-weight="700" font-family="'GT America Mono Light',monospace" text-anchor="middle" dominant-baseline="middle">-</text>
<polygon points="-8.09,290.27 -420,53.04 -420,140.19 -8.09,377.43" fill="rgba(255,232,232,0.45)" stroke="#bd3a3a" stroke-width="1.5"/>
<g transform="scale(-1,1) translate(-280,0)" fill="none" stroke="#1b1d1f" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M 88.93,377.25 A 0.34,0.19 -125.13 0,1 88.75,377.25"/>
<path d="M 88.61,377.52 A 0.34,0.19 -180.00 0,1 88.70,377.66"/>
<path d="M 88.94,377.79 A 0.34,0.19 -60.06 0,1 89.18,377.40"/>
<path d="M 88.70,378.31 A 0.15,0.08 -60.06 0,1 88.73,378.38"/>
<path d="M 88.71,378.04 A 0.34,0.19 -179.05 0,1 88.61,378.17"/>
<path d="M 92.80,379.48 A 0.34,0.19 -60.06 0,1 92.58,379.89"/>
<path d="M 92.58,379.89 A 0.34,0.19 -0.00 0,1 93.04,379.89"/>
<path d="M 93.04,379.89 A 0.34,0.17 -119.95 0,1 92.80,379.48"/>
<path d="M 89.35,378.03 A 0.34,0.19 -60.06 0,1 89.58,377.63"/>
<path d="M 89.14,378.17 A 0.34,0.21 -5.79 0,1 89.20,378.01"/>
<path d="M 89.53,377.77 A 0.34,0.27 -0.00 0,1 89.21,378.03"/>
<path d="M 89.53,377.77 A 0.67,0.41 -2.74 0,1 89.33,377.49"/>
<path d="M 93.94,380.17 A 0.15,0.09 -54.27 0,1 93.87,380.17"/>
<path d="M 94.17,380.11 A 0.15,0.08 -0.00 0,1 93.97,380.12"/>
<path d="M 94.17,380.24 A 0.29,0.18 -57.32 0,1 93.96,380.11"/>
<path d="M 93.92,380.18 A 0.15,0.08 -115.65 0,1 93.96,380.11"/>
<path d="M 89.21,378.78 L 89.21,378.74 L 89.20,378.69 L 89.17,378.61 L 89.14,378.58 L 89.11,378.55"/>
<path d="M 89.12,378.61 A 0.15,0.07 -53.02 0,1 89.18,378.61"/>
<path d="M 89.04,378.55 A 0.15,0.08 -60.06 0,1 89.11,378.54"/>
<path d="M 89.11,378.55 A 0.92,0.53 -119.94 0,1 89.01,378.49"/>
<path d="M 88.61,379.67 A 0.34,0.20 -0.99 0,1 88.70,379.81"/>
<path d="M 88.80,379.63 A 0.15,0.08 -60.06 0,1 88.70,379.81"/>
<path d="M 88.80,379.47 A 0.19,0.11 -1.79 0,1 88.80,379.63"/>
<path d="M 88.72,379.47 A 0.15,0.09 -124.30 0,1 88.69,379.55"/>
<path d="M 92.93,380.60 A 0.15,0.08 -115.65 0,1 93.00,380.62"/>
<path d="M 93.04,380.27 A 0.34,0.19 -179.05 0,1 92.58,380.27"/>
<path d="M 92.61,380.61 A 0.15,0.08 -60.06 0,1 92.68,380.60"/>
<path d="M 91.98,380.03 A 0.34,0.21 -5.79 0,1 92.20,379.93"/>
<path d="M 92.40,379.25 A 0.34,0.19 -60.06 0,1 92.17,379.65"/>
<path d="M 92.40,379.25 A 0.67,0.41 -2.74 0,1 91.92,379.15"/>
<path d="M 93.96,381.53 A 0.29,0.18 -57.32 0,1 94.17,381.18"/>
<path d="M 93.97,381.18 A 0.15,0.08 -180.00 0,1 94.17,381.18"/>
<path d="M 93.87,381.10 A 0.15,0.09 -54.27 0,1 93.74,381.26"/>
<path d="M 93.96,381.53 A 0.15,0.08 -119.11 0,1 93.86,381.35"/>
<path d="M 93.37,380.83 A 0.15,0.08 -60.06 0,1 93.26,381.01"/>
<path d="M 92.98,380.63 A 0.72,0.42 -119.94 0,1 93.37,381.07"/>
<path d="M 93.88,380.66 A 0.57,0.33 -119.94 0,1 93.73,380.22"/>
<path d="M 88.70,384.92 A 0.15,0.08 -60.06 0,1 88.73,384.99"/>
<path d="M 88.71,384.65 A 0.34,0.19 -179.05 0,1 88.61,384.78"/>
<path d="M 89.11,380.03 L 89.13,380.04 L 89.15,380.04 L 89.18,380.05 L 89.19,380.06 L 89.20,380.07 L 89.21,380.07 L 89.21,380.07"/>
<path d="M 89.21,379.85 L 89.20,379.85 L 89.20,379.85 L 89.19,379.85"/>
<path d="M 89.25,380.09 A 0.15,0.10 -68.26 0,1 89.31,379.89"/>
<path d="M 89.11,379.81 A 0.15,0.08 -60.06 0,1 89.01,379.98"/>
<path d="M 89.01,379.98 A 0.92,0.53 -119.94 0,1 89.11,380.03"/>
<path d="M 89.65,379.58 A 0.77,0.45 -119.94 0,1 89.66,379.49"/>
<path d="M 92.17,380.31 A 0.15,0.10 -68.26 0,1 92.22,380.39"/>
<path d="M 92.17,380.31 L 92.15,380.31 L 92.13,380.31 L 92.08,380.31 L 92.04,380.31 L 91.98,380.33 L 91.96,380.34 L 91.94,380.35"/>
<path d="M 92.58,382.04 A 0.34,0.20 -0.99 0,1 93.04,382.04"/>
<path d="M 92.84,381.91 A 0.15,0.08 -119.11 0,1 92.94,382.09"/>
<path d="M 92.93,381.69 A 0.19,0.11 -1.79 0,1 92.67,381.71"/>
<path d="M 92.68,382.10 A 0.15,0.08 -60.06 0,1 92.79,381.92"/>
<path d="M 93.94,386.78 A 0.15,0.09 -54.27 0,1 93.87,386.78"/>
<path d="M 94.17,386.72 A 0.15,0.08 -0.00 0,1 93.97,386.73"/>
<path d="M 94.17,386.85 A 0.29,0.18 -57.32 0,1 93.96,386.72"/>
<path d="M 93.92,386.79 A 0.15,0.08 -115.65 0,1 93.96,386.72"/>
<path d="M 89.21,385.39 L 89.21,385.35 L 89.20,385.30 L 89.17,385.22 L 89.14,385.19 L 89.11,385.16"/>
<path d="M 89.12,385.22 A 0.15,0.07 -53.02 0,1 89.18,385.22"/>
<path d="M 89.04,385.16 A 0.15,0.08 -60.06 0,1 89.11,385.16"/>
<path d="M 89.11,385.16 A 0.92,0.53 -119.94 0,1 89.01,385.10"/>
<path d="M 88.61,386.28 A 0.34,0.20 -0.99 0,1 88.70,386.42"/>
<path d="M 88.80,386.24 A 0.15,0.08 -60.06 0,1 88.70,386.42"/>
<path d="M 88.80,386.08 A 0.19,0.11 -1.79 0,1 88.80,386.24"/>
<path d="M 88.72,386.08 A 0.15,0.09 -124.30 0,1 88.69,386.16"/>
<path d="M 92.93,387.21 A 0.15,0.08 -115.65 0,1 93.00,387.23"/>
<path d="M 93.04,386.88 A 0.34,0.19 -179.05 0,1 92.58,386.88"/>
<path d="M 92.61,387.22 A 0.15,0.08 -60.06 0,1 92.68,387.21"/>
<path d="M 92.17,381.80 A 1.24,1.24 0 0,1 91.94,381.64"/>
<path d="M 92.29,381.63 A 0.15,0.07 -53.02 0,1 92.17,381.80"/>
<path d="M 92.29,381.63 A 0.89,0.89 0 0,1 91.99,381.49"/>
<path d="M 92.37,381.68 A 0.15,0.08 -60.06 0,1 92.27,381.86"/>
<path d="M 92.17,381.80 A 0.92,0.53 -119.94 0,1 92.27,381.86"/>
<path d="M 92.91,381.37 A 0.77,0.45 -119.94 0,1 92.91,381.27"/>
<path d="M 91.94,380.35 A 1.28,0.74 -180.00 0,1 91.94,381.64"/>
<path d="M 91.99,381.49 L 91.93,381.48 L 91.88,381.46 L 91.71,381.40 L 91.61,381.34 L 91.47,381.22 L 91.43,381.16 L 91.39,381.06 L 91.39,381.02 L 91.39,380.98 L 91.39,380.94 L 91.39,380.90 L 91.43,380.79 L 91.47,380.73 L 91.60,380.60 L 91.68,380.55 L 91.78,380.49"/>
<path d="M 93.96,388.14 A 0.29,0.18 -57.32 0,1 94.17,387.79"/>
<path d="M 93.97,387.79 A 0.15,0.08 -180.00 0,1 94.17,387.79"/>
<path d="M 93.87,387.71 A 0.15,0.09 -54.27 0,1 93.74,387.87"/>
<path d="M 93.96,388.14 A 0.15,0.08 -119.11 0,1 93.86,387.96"/>
<path d="M 93.37,387.68 A 0.15,0.08 -60.06 0,1 93.47,387.50"/>
<path d="M 93.88,387.27 A 0.57,0.33 -119.94 0,1 93.73,386.83"/>
<path d="M 92.98,387.24 A 0.72,0.42 -119.94 0,1 93.37,387.68"/>
<path d="M 88.77,387.22 A 0.34,0.19 -60.06 0,1 88.94,387.21"/>
<path d="M 88.70,386.80 A 0.34,0.19 -180.00 0,1 88.61,386.93"/>
<path d="M 89.11,386.64 L 89.13,386.65 L 89.15,386.65 L 89.18,386.67 L 89.19,386.67 L 89.20,386.68 L 89.21,386.68 L 89.21,386.68"/>
<path d="M 89.21,386.46 L 89.20,386.46 L 89.20,386.46 L 89.19,386.46"/>
<path d="M 89.25,386.70 A 0.15,0.10 -68.26 0,1 89.31,386.50"/>
<path d="M 89.11,386.42 A 0.15,0.08 -60.06 0,1 89.01,386.59"/>
<path d="M 89.01,386.59 A 0.92,0.53 -119.94 0,1 89.11,386.64"/>
<path d="M 89.65,386.19 A 0.77,0.45 -119.94 0,1 89.66,386.10"/>
<path d="M 92.17,386.92 A 0.15,0.10 -68.26 0,1 92.22,387.00"/>
<path d="M 92.17,386.92 L 92.15,386.92 L 92.13,386.92 L 92.08,386.92 L 92.04,386.92 L 91.98,386.94 L 91.96,386.95 L 91.94,386.97"/>
<path d="M 92.93,388.30 A 0.19,0.11 -1.79 0,1 92.67,388.32"/>
<path d="M 92.68,388.71 A 0.15,0.08 -60.06 0,1 92.79,388.53"/>
<path d="M 92.58,388.65 A 0.34,0.20 -0.99 0,1 93.04,388.65"/>
<path d="M 92.84,388.52 A 0.15,0.08 -119.11 0,1 92.94,388.70"/>
<path d="M 92.96,389.47 A 0.34,0.19 -114.75 0,1 93.04,389.31"/>
<path d="M 93.04,389.02 A 0.34,0.19 -0.00 0,1 92.58,389.03"/>
<path d="M 92.58,389.30 A 0.34,0.19 -60.06 0,1 92.65,389.45"/>
<path d="M 89.21,387.43 A 0.34,0.17 -175.29 0,1 89.11,387.30"/>
<path d="M 89.18,387.46 A 0.34,0.19 -60.06 0,1 89.35,387.44"/>
<path d="M 92.29,388.24 A 0.89,0.89 0 0,1 91.99,388.10"/>
<path d="M 92.17,388.41 A 1.24,1.24 0 0,1 91.94,388.25"/>
<path d="M 92.29,388.24 A 0.15,0.07 -53.02 0,1 92.17,388.41"/>
<path d="M 91.94,386.97 A 1.28,0.74 -180.00 0,1 91.94,388.25"/>
<path d="M 91.99,388.10 L 91.93,388.09 L 91.88,388.07 L 91.71,388.01 L 91.61,387.95 L 91.47,387.83 L 91.43,387.77 L 91.39,387.67 L 91.39,387.63 L 91.39,387.59 L 91.39,387.55 L 91.39,387.51 L 91.43,387.40 L 91.47,387.34 L 91.60,387.21 L 91.68,387.16 L 91.78,387.10"/>
<path d="M 92.17,389.07 A 0.34,0.19 -60.06 0,1 92.24,389.22"/>
<path d="M 92.17,389.07 A 0.34,0.17 -175.29 0,1 91.94,389.00"/>
<path d="M 91.09,386.98 A 1.81,1.00 -177.97 0,1 89.21,387.89"/>
<path d="M 92.37,388.29 A 0.15,0.08 -60.06 0,1 92.27,388.47"/>
<path d="M 92.17,388.41 A 0.92,0.53 -119.94 0,1 92.27,388.47"/>
<path d="M 92.91,387.98 A 0.77,0.45 -119.94 0,1 92.91,387.88"/>
<path d="M 94.38,388.81 A 0.34,0.19 -119.94 0,1 94.46,388.65"/>
<path d="M 92.30,387.04 A 0.15,0.08 -60.06 0,1 92.37,387.03"/>
<path d="M 92.27,386.97 A 0.92,0.53 -119.94 0,1 92.17,386.92"/>
<path d="M 92.30,380.43 A 0.15,0.08 -60.06 0,1 92.37,380.42"/>
<path d="M 92.27,380.36 A 0.92,0.53 -119.94 0,1 92.17,380.31"/>
<path d="M 89.21,378.59 A 0.34,0.27 -0.00 0,1 89.53,378.86"/>
<path d="M 90.92,377.39 A 1.47,0.89 -2.74 0,1 89.53,378.38"/>
<path d="M 89.21,378.59 A 1.81,1.09 -2.21 0,1 91.02,379.63"/>
<path d="M 94.46,379.08 A 0.34,0.19 -119.94 0,1 94.22,378.67"/>
<path d="M 90.20,376.36 A 0.34,0.19 -119.94 0,1 90.03,376.34"/>
<path d="M 93.29,380.52 A 0.36,0.21 -119.94 1,1 93.63,380.28"/>
<path d="M 93.29,387.13 A 0.36,0.21 -119.94 1,1 93.63,386.89"/>
<path d="M 88.73,378.38 L 88.72,378.37 L 88.71,378.36 L 88.69,378.34 L 88.67,378.33 L 88.65,378.31 L 88.64,378.29 L 88.63,378.26 L 88.62,378.25 L 88.61,378.21 L 88.61,378.19 L 88.61,378.17"/>
<path d="M 93.92,380.18 L 93.93,380.18 L 93.93,380.18 L 93.94,380.17 L 93.94,380.17 L 93.95,380.17 L 93.95,380.17 L 93.95,380.17"/>
<path d="M 89.21,378.78 L 89.21,378.77 L 89.21,378.76 L 89.20,378.73 L 89.20,378.72 L 89.19,378.69 L 89.18,378.68 L 89.17,378.65 L 89.16,378.64 L 89.14,378.62 L 89.14,378.62 L 89.12,378.61"/>
<path d="M 89.12,378.61 A 0.81,0.81 0 0,1 89.04,378.55"/>
<path d="M 88.72,379.47 L 88.71,379.48 L 88.69,379.49 L 88.67,379.51 L 88.66,379.52 L 88.64,379.54 L 88.64,379.56 L 88.62,379.59 L 88.62,379.60 L 88.61,379.63 L 88.61,379.65 L 88.61,379.67"/>
<path d="M 93.00,380.62 L 92.98,380.63 L 92.95,380.64 L 92.90,380.65 L 92.87,380.66 L 92.82,380.66 L 92.79,380.66 L 92.74,380.65 L 92.71,380.65 L 92.66,380.63 L 92.63,380.62 L 92.61,380.61"/>
<path d="M 88.73,384.99 L 88.72,384.98 L 88.71,384.97 L 88.69,384.95 L 88.67,384.94 L 88.65,384.92 L 88.64,384.90 L 88.63,384.87 L 88.62,384.86 L 88.61,384.82 L 88.61,384.80 L 88.61,384.78"/>
<path d="M 92.22,380.39 L 92.21,380.39 L 92.20,380.38 L 92.18,380.38 L 92.16,380.38 L 92.13,380.38 L 92.12,380.38 L 92.08,380.38 L 92.06,380.39 L 92.02,380.40 L 92.00,380.40 L 91.98,380.41"/>
<path d="M 93.92,386.79 L 93.93,386.79 L 93.93,386.79 L 93.94,386.78 L 93.94,386.78 L 93.95,386.78 L 93.95,386.78 L 93.95,386.78"/>
<path d="M 89.21,385.39 L 89.21,385.38 L 89.21,385.37 L 89.20,385.34 L 89.20,385.33 L 89.19,385.30 L 89.18,385.29 L 89.17,385.26 L 89.16,385.25 L 89.14,385.23 L 89.14,385.23 L 89.12,385.22"/>
<path d="M 89.12,385.22 A 0.81,0.81 0 0,1 89.04,385.16"/>
<path d="M 88.72,386.08 L 88.71,386.09 L 88.69,386.10 L 88.67,386.12 L 88.66,386.13 L 88.64,386.15 L 88.64,386.17 L 88.62,386.20 L 88.62,386.21 L 88.61,386.24 L 88.61,386.26 L 88.61,386.28"/>
<path d="M 93.00,387.23 L 92.98,387.24 L 92.95,387.25 L 92.90,387.26 L 92.87,387.27 L 92.82,387.27 L 92.79,387.27 L 92.74,387.26 L 92.71,387.26 L 92.66,387.24 L 92.63,387.23 L 92.61,387.22"/>
<path d="M 92.22,387.00 L 92.21,387.00 L 92.20,386.99 L 92.18,386.99 L 92.16,386.99 L 92.13,386.99 L 92.12,386.99 L 92.08,386.99 L 92.06,387.00 L 92.02,387.01 L 92.00,387.01 L 91.98,387.02"/>
<path d="M 89.21,387.48 L 89.20,387.47 L 89.19,387.46 L 89.18,387.46"/>
<path d="M 92.24,389.22 L 92.23,389.21 L 92.21,389.21 L 92.18,389.19 L 92.16,389.18 L 92.12,389.17 L 92.10,389.17 L 92.05,389.16 L 92.03,389.15 L 91.98,389.15 L 91.95,389.14 L 91.92,389.14"/>
<path d="M 91.01,389.12 L 90.83,389.11 L 90.66,389.09 L 90.33,389.02 L 90.17,388.97 L 89.87,388.85 L 89.74,388.77 L 89.50,388.59 L 89.40,388.48 L 89.26,388.24 L 89.22,388.11 L 89.21,387.96 L 89.21,387.95 L 89.21,387.93"/>
<path d="M 92.30,387.04 A 0.62,0.62 0 0,1 92.22,387.00"/>
<path d="M 92.30,380.43 A 0.62,0.62 0 0,1 92.22,380.39"/>
<path d="M 92.04,390.00 L 91.84,390.74 L 91.65,390.43"/>
<path d="M 91.91,390.21 A 0.91,0.53 -180.00 0,1 90.12,390.21"/>
<path d="M 91.65,389.49 A 1.03,0.59 -180.00 0,1 89.99,389.96"/>
<path d="M 91.91,390.41 L 91.89,390.48 L 91.86,390.54 L 91.77,390.66 L 91.71,390.71 L 91.58,390.81 L 91.51,390.85 L 91.33,390.92 L 91.24,390.94 L 91.04,390.96 L 90.93,390.96 L 90.73,390.93 L 90.63,390.90 L 90.44,390.81 L 90.35,390.75 L 90.21,390.60 L 90.16,390.52 L 90.12,390.42 L 90.12,390.41 L 90.12,390.41"/>
<line x1="261.23" y1="369.01" x2="204.14" y2="336.13"/>
<line x1="185.56" y1="349.32" x2="261.23" y2="392.90"/>
<line x1="261.23" y1="395.47" x2="261.23" y2="392.90"/>
<line x1="183.32" y1="350.60" x2="261.23" y2="395.47"/>
<line x1="263.45" y1="396.74" x2="276.97" y2="388.96"/>
<line x1="263.45" y1="396.74" x2="261.23" y2="395.47"/>
<line x1="276.97" y1="111.89" x2="276.97" y2="388.96"/>
<line x1="276.97" y1="111.89" x2="274.74" y2="110.61"/>
<line x1="270.09" y1="115.85" x2="276.97" y2="111.89"/>
<line x1="268.28" y1="114.33" x2="274.74" y2="110.61"/>
<line x1="263.45" y1="396.74" x2="263.45" y2="126.96"/>
<line x1="261.23" y1="395.47" x2="261.23" y2="125.68"/>
<path d="M 177.17,62.16 A 0.26,0.15 -180.00 0,1 177.43,62.01"/>
<path d="M 163.85,53.90 A 0.25,0.14 -0.00 1,1 163.85,53.90"/>
<path d="M 169.14,53.62 A 0.25,0.14 -0.00 1,1 169.14,53.62"/>
<path d="M 166.25,55.28 A 0.25,0.14 -180.00 1,1 166.25,55.28"/>
<path d="M 166.74,52.24 A 0.25,0.14 -180.00 1,1 166.74,52.24"/>
<line x1="168.07" y1="49.18" x2="161.61" y2="52.90"/>
<line x1="168.07" y1="49.18" x2="274.74" y2="110.61"/>
<line x1="268.28" y1="114.33" x2="274.74" y2="110.61"/>
<line x1="159.39" y1="51.62" x2="165.85" y2="47.90"/>
<line x1="168.07" y1="49.18" x2="161.61" y2="52.90"/>
<line x1="165.85" y1="47.90" x2="168.07" y2="49.18"/>
<path d="M 101.69,117.56 A 0.26,0.15 -0.00 1,1 101.69,117.56"/>
<path d="M 94.73,110.57 A 0.26,0.15 -0.00 1,1 94.73,110.57"/>
<path d="M 102.67,120.31 A 0.26,0.15 -0.00 1,1 102.67,120.31"/>
<path d="M 99.78,121.98 A 0.26,0.15 -180.00 1,1 99.78,121.98"/>
<path d="M 91.95,112.18 A 0.26,0.15 -0.00 1,1 91.95,112.18"/>
<path d="M 104.08,116.18 A 0.26,0.15 -0.00 1,1 104.08,116.18"/>
<path d="M 95.92,110.57 A 0.26,0.15 -0.00 1,1 95.92,110.57"/>
<path d="M 101.19,114.52 A 0.26,0.15 -180.00 1,1 101.19,114.52"/>
<path d="M 100.28,118.94 A 0.26,0.15 -0.00 1,1 100.28,118.94"/>
<path d="M 97.39,120.60 A 0.26,0.15 -0.00 1,1 97.39,120.60"/>
<path d="M 95.33,113.27 A 1.57,0.90 -0.00 1,1 95.33,113.27"/>
<path d="M 98.80,115.89 A 0.26,0.15 -0.00 1,1 98.80,115.89"/>
<path d="M 98.70,112.18 A 0.26,0.15 -0.00 1,1 98.70,112.18"/>
<line x1="200.44" y1="90.67" x2="200.44" y2="340.75"/>
<line x1="200.44" y1="340.75" x2="201.92" y2="341.60"/>
<line x1="201.92" y1="341.60" x2="201.92" y2="91.52"/>
<line x1="204.14" y1="340.33" x2="204.14" y2="92.80"/>
<line x1="204.14" y1="340.33" x2="201.92" y2="341.60"/>
<line x1="155.26" y1="64.66" x2="155.26" y2="64.66"/>
<line x1="144.15" y1="58.26" x2="144.15" y2="58.26"/>
<line x1="155.26" y1="64.66" x2="144.15" y2="58.26"/>
<line x1="155.26" y1="64.66" x2="155.26" y2="70.62"/>
<line x1="157.49" y1="69.34" x2="157.49" y2="65.94"/>
<line x1="144.15" y1="58.26" x2="144.15" y2="77.02"/>
<line x1="144.89" y1="43.28" x2="142.67" y2="44.56"/>
<line x1="142.67" y1="44.56" x2="142.67" y2="57.41"/>
<line x1="267.86" y1="116.65" x2="142.67" y2="44.56"/>
<line x1="267.86" y1="129.50" x2="162.65" y2="68.91"/>
<line x1="160.44" y1="67.64" x2="142.67" y2="57.41"/>
<line x1="267.86" y1="116.65" x2="267.86" y2="129.50"/>
<line x1="270.09" y1="115.37" x2="267.86" y2="116.65"/>
<line x1="270.09" y1="128.22" x2="267.86" y2="129.50"/>
<line x1="270.09" y1="115.37" x2="270.09" y2="128.22"/>
<line x1="144.89" y1="43.28" x2="270.09" y2="115.37"/>
<line x1="161.75" y1="50.26" x2="156.96" y2="47.50"/>
<line x1="156.96" y1="47.50" x2="156.96" y2="50.23"/>
<line x1="155.48" y1="33.80" x2="157.70" y2="32.52"/>
<line x1="155.48" y1="46.65" x2="155.48" y2="33.80"/>
<line x1="287.34" y1="109.74" x2="155.48" y2="33.80"/>
<line x1="155.48" y1="46.65" x2="161.75" y2="50.26"/>
<line x1="157.70" y1="32.52" x2="289.56" y2="108.46"/>
<line x1="285.86" y1="121.73" x2="276.97" y2="116.61"/>
<line x1="285.86" y1="394.08" x2="276.97" y2="388.96"/>
<line x1="285.88" y1="394.00" x2="285.88" y2="121.72"/>
<line x1="288.09" y1="122.15" x2="288.09" y2="392.86"/>
<line x1="287.35" y1="109.72" x2="287.35" y2="122.58"/>
<line x1="289.53" y1="121.28" x2="289.53" y2="108.45"/>
<line x1="288.09" y1="392.86" x2="285.88" y2="394.00"/>
<line x1="289.53" y1="108.45" x2="287.35" y2="109.72"/>
<line x1="287.35" y1="122.58" x2="289.53" y2="121.28"/>
<line x1="88.70" y1="378.31" x2="88.70" y2="377.66"/>
<line x1="89.11" y1="377.89" x2="89.11" y2="378.55"/>
<line x1="89.01" y1="378.49" x2="88.70" y2="378.31"/>
<line x1="92.58" y1="379.89" x2="92.58" y2="380.54"/>
<line x1="93.04" y1="380.55" x2="93.04" y2="379.89"/>
<line x1="94.17" y1="381.18" x2="94.17" y2="380.24"/>
<line x1="93.97" y1="380.24" x2="93.97" y2="381.18"/>
<line x1="93.37" y1="381.07" x2="93.86" y2="381.35"/>
<line x1="93.97" y1="381.18" x2="93.47" y2="380.89"/>
<line x1="93.89" y1="380.19" x2="93.97" y2="380.24"/>
<line x1="88.70" y1="384.92" x2="88.70" y2="379.81"/>
<line x1="88.70" y1="379.81" x2="89.01" y2="379.98"/>
<line x1="89.11" y1="379.81" x2="88.80" y2="379.63"/>
<line x1="91.94" y1="379.61" x2="91.94" y2="380.35"/>
<line x1="92.17" y1="380.31" x2="92.17" y2="379.65"/>
<line x1="93.04" y1="382.04" x2="93.96" y2="381.53"/>
<line x1="93.86" y1="381.35" x2="92.94" y2="381.86"/>
<line x1="89.11" y1="380.03" x2="89.11" y2="385.16"/>
<line x1="89.01" y1="385.10" x2="88.70" y2="384.92"/>
<line x1="92.58" y1="382.04" x2="92.58" y2="387.15"/>
<line x1="93.04" y1="387.16" x2="93.04" y2="382.04"/>
<line x1="92.27" y1="381.86" x2="92.58" y2="382.04"/>
<line x1="92.68" y1="381.86" x2="92.37" y2="381.68"/>
<line x1="94.17" y1="387.79" x2="94.17" y2="386.85"/>
<line x1="93.97" y1="386.85" x2="93.97" y2="387.79"/>
<line x1="93.89" y1="386.80" x2="93.97" y2="386.85"/>
<line x1="93.97" y1="387.79" x2="93.47" y2="387.50"/>
<line x1="93.37" y1="387.68" x2="93.86" y2="387.96"/>
<line x1="88.70" y1="387.07" x2="88.70" y2="386.42"/>
<line x1="88.70" y1="386.42" x2="89.01" y2="386.59"/>
<line x1="89.11" y1="386.42" x2="88.80" y2="386.24"/>
<line x1="91.94" y1="381.64" x2="91.94" y2="386.97"/>
<line x1="92.17" y1="386.92" x2="92.17" y2="381.80"/>
<line x1="92.58" y1="388.65" x2="92.58" y2="389.30"/>
<line x1="93.04" y1="389.31" x2="93.04" y2="388.65"/>
<line x1="89.11" y1="386.64" x2="89.11" y2="387.30"/>
<line x1="89.11" y1="387.30" x2="88.70" y2="387.07"/>
<line x1="91.94" y1="388.25" x2="91.94" y2="389.00"/>
<line x1="92.17" y1="389.07" x2="92.17" y2="388.41"/>
<line x1="91.94" y1="389.00" x2="91.02" y2="388.97"/>
<line x1="92.58" y1="389.30" x2="92.17" y2="389.07"/>
<line x1="92.27" y1="388.47" x2="92.58" y2="388.65"/>
<line x1="92.68" y1="388.47" x2="92.37" y2="388.29"/>
<line x1="94.46" y1="388.65" x2="93.04" y2="389.31"/>
<line x1="93.04" y1="388.65" x2="93.96" y2="388.14"/>
<line x1="93.86" y1="387.96" x2="92.94" y2="388.47"/>
<line x1="92.58" y1="387.15" x2="92.27" y2="386.97"/>
<line x1="93.96" y1="386.72" x2="93.04" y2="387.16"/>
<line x1="88.80" y1="386.09" x2="89.21" y2="385.80"/>
<line x1="92.58" y1="380.54" x2="92.27" y2="380.36"/>
<line x1="92.80" y1="379.48" x2="92.40" y2="379.25"/>
<line x1="92.17" y1="379.65" x2="92.58" y2="379.89"/>
<line x1="89.53" y1="378.33" x2="89.53" y2="377.77"/>
<line x1="89.21" y1="378.03" x2="89.21" y2="378.59"/>
<line x1="91.92" y1="379.15" x2="91.00" y2="379.17"/>
<line x1="91.02" y1="379.63" x2="91.94" y2="379.61"/>
<line x1="93.96" y1="380.11" x2="93.04" y2="380.55"/>
<line x1="88.80" y1="379.48" x2="89.21" y2="379.19"/>
<line x1="89.33" y1="377.49" x2="88.93" y2="377.25"/>
<line x1="88.70" y1="377.66" x2="89.11" y2="377.89"/>
<line x1="94.22" y1="378.67" x2="92.80" y2="379.48"/>
<line x1="93.04" y1="379.89" x2="94.46" y2="379.08"/>
<line x1="88.93" y1="377.25" x2="90.20" y2="376.36"/>
<line x1="89.21" y1="379.75" x2="89.11" y2="379.81"/>
<line x1="91.02" y1="379.63" x2="91.02" y2="388.97"/>
<line x1="89.21" y1="387.93" x2="89.21" y2="378.59"/>
<line x1="89.21" y1="386.36" x2="89.11" y2="386.42"/>
<line x1="94.22" y1="378.67" x2="90.20" y2="376.36"/>
<line x1="94.46" y1="388.65" x2="94.46" y2="379.08"/>
<line x1="93.37" y1="387.68" x2="92.37" y2="388.29"/>
<line x1="93.37" y1="381.07" x2="92.37" y2="381.68"/>
<line x1="88.61" y1="378.17" x2="88.61" y2="377.52"/>
<line x1="88.73" y1="378.38" x2="89.04" y2="378.55"/>
<line x1="88.61" y1="384.78" x2="88.61" y2="379.67"/>
<line x1="88.73" y1="384.99" x2="89.04" y2="385.16"/>
<line x1="88.61" y1="386.93" x2="88.61" y2="386.28"/>
<line x1="89.18" y1="387.46" x2="88.77" y2="387.22"/>
<line x1="91.92" y1="389.14" x2="91.01" y2="389.12"/>
<line x1="92.65" y1="389.45" x2="92.24" y2="389.22"/>
<line x1="94.38" y1="388.81" x2="92.96" y2="389.47"/>
<line x1="92.30" y1="387.04" x2="92.61" y2="387.22"/>
<line x1="93.00" y1="387.23" x2="93.92" y2="386.79"/>
<line x1="88.72" y1="386.08" x2="89.21" y2="385.75"/>
<line x1="92.30" y1="380.43" x2="92.61" y2="380.61"/>
<line x1="93.00" y1="380.62" x2="93.92" y2="380.18"/>
<line x1="88.72" y1="379.47" x2="89.21" y2="379.14"/>
<line x1="90.03" y1="376.34" x2="88.75" y2="377.25"/>
<line x1="91.65" y1="390.43" x2="91.65" y2="389.14"/>
<line x1="92.04" y1="389.16" x2="92.04" y2="390.00"/>
<line x1="90.12" y1="390.41" x2="90.01" y2="390.07"/>
<line x1="91.91" y1="390.41" x2="91.98" y2="390.21"/>
<line x1="89.99" y1="388.90" x2="89.99" y2="389.96"/>
<line x1="92.04" y1="389.16" x2="92.04" y2="389.96"/>
<line x1="106.17" y1="126.75" x2="106.17" y2="126.97"/>
<line x1="104.96" y1="127.44" x2="106.17" y2="126.75"/>
<line x1="106.63" y1="128.40" x2="104.96" y2="127.44"/>
<line x1="107.84" y1="127.71" x2="106.63" y2="128.40"/>
<line x1="107.84" y1="127.71" x2="109.32" y2="128.56"/>
<line x1="109.32" y1="128.56" x2="109.32" y2="394.93"/>
<line x1="84.87" y1="114.49" x2="106.17" y2="126.75"/>
<line x1="107.10" y1="396.21" x2="109.32" y2="394.93"/>
<line x1="82.65" y1="382.13" x2="89.08" y2="385.83"/>
<line x1="94.31" y1="388.84" x2="107.10" y2="396.21"/>
<line x1="107.10" y1="129.84" x2="107.10" y2="396.21"/>
<line x1="107.10" y1="129.84" x2="109.32" y2="128.56"/>
<line x1="82.65" y1="115.77" x2="84.87" y2="114.49"/>
<line x1="82.65" y1="115.77" x2="107.10" y2="129.84"/>
<line x1="82.65" y1="382.13" x2="82.65" y2="115.77"/>
<line x1="106.26" y1="126.80" x2="184.72" y2="81.62"/>
<line x1="106.26" y1="126.91" x2="106.26" y2="126.80"/>
<line x1="84.87" y1="114.49" x2="106.26" y2="126.80"/>
<line x1="84.87" y1="114.49" x2="163.33" y2="69.30"/>
<line x1="186.30" y1="82.53" x2="106.63" y2="128.40"/>
<line x1="105.15" y1="127.55" x2="184.82" y2="81.67"/>
<line x1="106.63" y1="128.40" x2="105.15" y2="127.55"/>
<line x1="109.32" y1="393.22" x2="200.44" y2="340.75"/>
<line x1="79.69" y1="116.62" x2="77.54" y2="115.38"/>
<line x1="79.69" y1="116.62" x2="86.36" y2="112.78"/>
<line x1="84.21" y1="111.54" x2="86.36" y2="112.78"/>
<line x1="77.54" y1="115.38" x2="84.21" y2="111.54"/>
<line x1="77.54" y1="384.31" x2="77.54" y2="115.38"/>
<line x1="79.69" y1="385.55" x2="77.54" y2="384.31"/>
<line x1="79.69" y1="385.55" x2="84.14" y2="382.99"/>
<line x1="79.69" y1="116.62" x2="79.69" y2="385.55"/>
<line x1="86.36" y1="112.78" x2="86.36" y2="113.63"/>
<line x1="84.21" y1="111.54" x2="160.50" y2="67.60"/>
<line x1="86.36" y1="112.78" x2="86.36" y2="113.63"/>
<line x1="84.21" y1="111.54" x2="86.36" y2="112.78"/>
<line x1="162.65" y1="68.84" x2="86.36" y2="112.78"/>
<line x1="76.80" y1="113.25" x2="79.02" y2="114.53"/>
<line x1="65.69" y1="119.64" x2="76.80" y2="113.25"/>
<line x1="67.91" y1="120.92" x2="65.69" y2="119.64"/>
<line x1="79.02" y1="114.53" x2="67.91" y2="120.92"/>
<line x1="67.91" y1="120.92" x2="67.91" y2="393.20"/>
<line x1="67.91" y1="393.20" x2="79.02" y2="386.80"/>
<line x1="79.02" y1="385.17" x2="79.02" y2="386.80"/>
<line x1="65.69" y1="391.92" x2="65.69" y2="119.64"/>
<line x1="67.91" y1="393.20" x2="65.69" y2="391.92"/>
</g>
</svg>`,
  };
  var SVG_LABELS = {
    'lh-in-iso':
      'Left-hand in-swing door isometric view showing pass-through space',
    'rh-in-iso':
      'Right-hand in-swing door isometric view showing pass-through space',
    'lh-out-iso':
      'Left-hand out-swing door isometric view showing pass-through space',
    'rh-out-iso':
      'Right-hand out-swing door isometric view showing pass-through space',
  };
  // ── HANDING / SWING RESOLUTION ────────────────────────────────────────

  /**
   * Resolve a diagram key from handing + swing.
   * @param {string} handing - 'lh' | 'rh'
   * @param {string} swing - 'in' | 'out'
   * @returns {string} e.g. 'lh-in-iso'
   */
  function resolveKey(handing, swing) {
    var h = String(handing || '').toLowerCase() === 'rh' ? 'rh' : 'lh';
    var s = String(swing || '').toLowerCase() === 'out' ? 'out' : 'in';
    return h + '-' + s + '-iso';
  }

  /**
   * Parse a human-readable handing label into { handing, swing }.
   *
   * Accepts BOTH established vocabularies so either caller can hand its own
   * native value straight in:
   *   - configurator "Door Handing" option values, verbatim from the PPD:
   *     'Left Hand Inswing' | 'Left Hand Outswing'
   *     | 'Right Hand Inswing' | 'Right Hand Outswing'
   *   - the widget's compact data-attribute form: 'LH-IS' | 'RH-OS' | …
   *
   * Substring matching (not equality) is deliberate: it survives casing,
   * punctuation and spacing drift in the Shopify option values, which are
   * merchant-editable. Returns null when nothing matches so callers can
   * distinguish "not chosen yet" from a real selection and show the neutral
   * placeholder diagram instead of silently defaulting to left-hand in-swing.
   *
   * @param {string} label
   * @returns {{handing: string, swing: string}|null}
   */
  function parseHandingLabel(label) {
    if (!label) return null;
    var s = String(label).toLowerCase();

    var handing = null;
    if (s.indexOf('right') !== -1 || /\brh\b/.test(s) || s.indexOf('rh-') === 0) {
      handing = 'rh';
    } else if (s.indexOf('left') !== -1 || /\blh\b/.test(s) || s.indexOf('lh-') === 0) {
      handing = 'lh';
    }

    var swing = null;
    // Check "out" first: 'outswing' contains 'swing', and '-os' must not be
    // shadowed by a looser in-swing test.
    if (s.indexOf('out') !== -1 || /-os\b/.test(s)) {
      swing = 'out';
    } else if (s.indexOf('in') !== -1 || /-is\b/.test(s)) {
      swing = 'in';
    }

    if (!handing || !swing) return null;
    return { handing: handing, swing: swing };
  }

  // ── DIAGRAM CREATION ──────────────────────────────────────────────────

  /**
   * Build a detached SVG element for the given handing + swing.
   *
   * DOMParser is inherently XSS-safe here: the parsed SVG is a detached
   * document, so scripts and event handlers in the markup never execute on
   * import. The markup is first-party build output regardless.
   *
   * @param {string} handing - 'lh' | 'rh'
   * @param {string} swing - 'in' | 'out'
   * @returns {SVGElement|null} detached SVG element, or null if key unknown
   */
  function createDiagram(handing, swing) {
    var key = resolveKey(handing, swing);
    var markup = SVGS[key];
    if (!markup) return null;

    var parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
    var svgEl = parsed.documentElement;
    if (!svgEl || svgEl.nodeName === 'parsererror') return null;

    svgEl.setAttribute('role', 'img');
    svgEl.setAttribute('aria-label', SVG_LABELS[key] || 'Door isometric view');

    /* The two pass-through floor bands ship as UNCLASSED <polygon> children with
       inline fill/stroke. Tag them here -- at the one factory every consumer goes
       through -- so a single stylesheet can own the palette instead of 87KB of
       hand-edited SVG strings. Inline fill/stroke are PRESENTATION attributes, so
       CSS wins without touching the markup. */
    var kids = svgEl.childNodes;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 1 && kids[i].nodeName.toLowerCase() === 'polygon') {
        kids[i].setAttribute('class', 'ptw-rug-band');
      } else if (
        kids[i].nodeType === 1 &&
        kids[i].nodeName.toLowerCase() === 'g' &&
        !kids[i].getAttribute('class')
      ) {
        /* The line-art groups carry the CAD stroke inline and their children
           inherit it, so tagging the GROUP is enough. Only UNCLASSED groups --
           .ptw-wc-annotation is a <g> too and keeps its own treatment. */
        kids[i].setAttribute('class', 'ptw-lineart');
      }
    }
    return svgEl;
  }

  /**
   * Write the red measurement labels into an already-created diagram.
   *
   * Callers pass DISPLAY STRINGS, not numbers — formatting is intentionally not
   * this library's job, so each surface keeps its own number style without one
   * changing the other.
   *
   * Wall clearance: out-swing diagrams carry no .ptw-wc-annotation group at all
   * (an out-swing door swings away from the wall, so the clearance is always 0
   * and was never drawn). Passing a falsy wallClearanceText hides the group
   * where one exists; a missing group is simply a no-op.
   *
   * @param {SVGElement} svgEl - element from createDiagram()
   * @param {Object} opts
   * @param {string} [opts.passThroughText] - e.g. '28″' or '—'
   * @param {string} [opts.wallClearanceText] - e.g. '1¾″'; falsy hides the annotation
   */
  function applyMeasurements(svgEl, opts) {
    if (!svgEl) return;
    var o = opts || {};

    var rugLabel = svgEl.querySelector('.ptw-rug-label');
    if (rugLabel && typeof o.passThroughText === 'string') {
      rugLabel.textContent = o.passThroughText;
    }

    var wcAnnotation = svgEl.querySelector('.ptw-wc-annotation');
    var wcApplied = false;
    if (wcAnnotation) {
      if (o.wallClearanceText) {
        wcAnnotation.style.display = '';
        var wcLabel = svgEl.querySelector('.ptw-wc-rug-label');
        if (wcLabel) wcLabel.textContent = o.wallClearanceText;
        wcApplied = true;
      } else {
        wcAnnotation.style.display = 'none';
      }
    }

    // Fold the live measurements into the accessible name.
    //
    // createDiagram() sets role='img', which makes the SVG ATOMIC to assistive
    // tech: its <text> children are not exposed, so the numbers written above
    // are invisible to a screen reader. They used to be reachable because the
    // tooltip also rendered them as plain HTML in .md-pts-readout, but that
    // duplicate was removed in 4eea4f0c, leaving the static SVG_LABELS string
    // ("...isometric view showing pass-through space") as the only accessible
    // name - i.e. the entire informational payload was unavailable to AT
    // (WCAG 1.1.1, Level A).
    //
    // Rebuilt from the base label each call so repeated applyMeasurements()
    // calls cannot accumulate stale numbers, and only when a value is present,
    // so the placeholder diagram keeps its own explanatory label.
    if (typeof o.passThroughText === 'string' && o.passThroughText) {
      var baseLabel =
        svgEl.getAttribute('data-md-base-label') ||
        svgEl.getAttribute('aria-label') ||
        'Door isometric view';
      svgEl.setAttribute('data-md-base-label', baseLabel);

      var spoken = baseLabel + '. Pass-through space ' + o.passThroughText + '.';
      if (wcApplied) {
        spoken += ' Wall clearance required ' + o.wallClearanceText + '.';
      }
      svgEl.setAttribute('aria-label', spoken);
    }
  }

  // ── PUBLIC API ────────────────────────────────────────────────────────
  window.MurphyDoorPassThroughDiagrams = {
    // math
    OS_SPECS: OS_SPECS,
    calcPassThrough: calcPassThrough,
    inSwingReduction: inSwingReduction,
    fmtInches16: fmtInches16,
    calcWallClearance: calcWallClearance,
    calcSwingClearance: calcSwingClearance,
    STANDARD_JAMB_DEPTH: STANDARD_JAMB_DEPTH,
    fmtInches: fmtInches,
    // geometry
    SVGS: SVGS,
    SVG_LABELS: SVG_LABELS,
    // helpers
    resolveKey: resolveKey,
    parseHandingLabel: parseHandingLabel,
    createDiagram: createDiagram,
    applyMeasurements: applyMeasurements,
  };
})(window);
